import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, RefreshCw, Link as LinkIcon, Activity,
  Clock, AlertCircle, Utensils, Dumbbell,
  Eye, MessageCircle, FileText, Camera, Ban, Trash2,
  X, ChevronDown, ChevronRight, Check, Pause, CircleOff,
  LayoutTemplate,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useClient } from '../context/ClientContext';
import AdminDashboardSkeleton from '../components/admin/AdminDashboardSkeleton';
import ClientAvatar from '../components/admin/ClientAvatar';
import PageHeader from '../components/common/PageHeader';
import API_BASE_URL from '../config/api';
import { authClient } from '../lib/authClient';
import { QRCodeSVG } from 'qrcode.react';
import { setTopbarClientsCache, deleteClient } from '../services/clientApi';
import {
  DEMO_CLIENT_ID,
  DEMO_USER_CODE,
  isTourDemoActive,
  withTourDemoClient,
} from '../services/tourDemo';


// Cache survives navigation and tab switches; refresh only on explicit Refresh or when cache is older than TTL.
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const ADMIN_DASHBOARD_SESSION_KEY = 'adminDashboardCache';

const dashboardCache = {
  clients: [],
  kpis: { activeEngagement: 0, quietZone: 0, silentZone: 0, expectedIncomeThisMonth: 0, usdToIlsRate: 3.7, exchangeRateSource: 'fallback' },
  lastUpdated: null,
  showClientAssignmentsColumn: false,
  companyName: null,
  assignableProviders: [],
  onboardingStepTotal: 16,
  userRole: null,
};

function readSessionCache() {
  try {
    const raw = sessionStorage.getItem(ADMIN_DASHBOARD_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (!parsed.clients?.length && !parsed.kpis?.exchangeRateSource)) return null;
    return {
      ...parsed,
      lastUpdated: parsed.lastUpdated ? new Date(parsed.lastUpdated) : null,
    };
  } catch {
    return null;
  }
}

function isCacheFresh(lastUpdated) {
  if (!lastUpdated) return false;
  return Date.now() - lastUpdated.getTime() < CACHE_TTL_MS;
}

function writeSessionCache(data) {
  try {
    const toStore = {
      clients: data.clients,
      kpis: data.kpis,
      lastUpdated: data.lastUpdated instanceof Date ? data.lastUpdated.toISOString() : data.lastUpdated,
      showClientAssignmentsColumn: data.showClientAssignmentsColumn,
      companyName: data.companyName,
      assignableProviders: data.assignableProviders || [],
      onboardingStepTotal: data.onboardingStepTotal ?? 16,
      userRole: data.userRole ?? null,
    };
    sessionStorage.setItem(ADMIN_DASHBOARD_SESSION_KEY, JSON.stringify(toStore));
  } catch (e) {
    console.warn('Admin dashboard: could not write session cache', e);
  }
}

/** Persists filter + search + sort across SPA navigations (same tab session). */
const ADMIN_DASHBOARD_UI_SESSION_KEY = 'adminDashboardUiPrefs';

const ADMIN_UI_DEFAULTS = {
  searchTerm: '',
  filterPulse: 'all',
  filterMealPlan: 'all',
  filterSubscription: 'all',
  filterWorkoutPlan: 'all',
  filterWeight: 'all',
  onboardingFilter: 'all',
  sortBy: '',
  sortOrder: 'asc',
};

/** KPI Silent = 72h+ or never. The card used to set filterPulse to 'silent', which matched no rows. */
function matchesPulseFilter(engagementPulse, filterPulse) {
  if (!filterPulse || filterPulse === 'all') return true;
  if (filterPulse === 'silent') {
    return engagementPulse === '72h' || engagementPulse === 'never';
  }
  if (filterPulse === 'onboarding') return engagementPulse === 'onboarding' || engagementPulse === 'new';
  return engagementPulse === filterPulse;
}

/**
 * The one thing a coach should do next for a client, most urgent first.
 * level: 'urgent' | 'attention' | 'info' | 'ok'. "Needs attention" = urgent or attention.
 */
function clientNextStep(client) {
  const sub = client?.subscription;
  const subStatus = String(sub?.status || '').toLowerCase();
  if (subStatus === 'past_due' || subStatus === 'unpaid') return { kind: 'payment', level: 'urgent' };
  if (client?.engagementPulse === '72h') return { kind: 'silent', level: 'urgent' };
  if (client?.engagementPulse === 'never') return { kind: 'never', level: 'urgent' };
  if (!client?.isOnboardingFinished) {
    if (client?.onboardingStuckAt) return { kind: 'stuck', level: 'attention', step: client.onboardingStuckAt.num };
    if (client?.engagementPulse === 'onboarding' || client?.engagementPulse === 'new') {
      return { kind: 'onboarding', level: 'info', step: client?.lastStep?.num || 0 };
    }
  }
  if (client?.mealPlanStatus !== 'active') return { kind: 'noMealPlan', level: 'attention' };
  if (client?.engagementPulse === '24h') return { kind: 'quiet', level: 'attention' };
  if (sub?.cancel_at_period_end) return { kind: 'cancelling', level: 'attention' };
  const lastWeigh = client?.lastWeightUpdate ? new Date(client.lastWeightUpdate) : null;
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  if (!lastWeigh || lastWeigh < weekStart) return { kind: 'noWeighIn', level: 'info' };
  return { kind: 'ok', level: 'ok' };
}
const NEXT_STEP_RANK = { urgent: 0, attention: 1, info: 2, ok: 3 };
const needsAttention = (client) => NEXT_STEP_RANK[clientNextStep(client).level] <= 1;

function readAdminUiPrefs() {
  try {
    const raw = sessionStorage.getItem(ADMIN_DASHBOARD_UI_SESSION_KEY);
    if (!raw) return { ...ADMIN_UI_DEFAULTS };
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') return { ...ADMIN_UI_DEFAULTS };

    const pulseOk = ['all', 'attention', 'healthy', '24h', '72h', 'never', 'silent', 'onboarding'];
    const mealOk = ['all', 'active', 'not_active', 'none'];
    const subOk = ['all', 'active', 'canceled', 'ends_this_month'];
    const workoutOk = ['all', 'active', 'not_active', 'none'];
    const weightOk = ['all', 'ok', 'missing_week', 'never'];
    const onboardOk = ['all', 'incomplete'];
    const sortByOk = ['', 'name', 'mealPlanStatus', 'statusUpdated', 'weight', 'workoutPlan', 'payment', 'subscription', 'engagementPulse'];
    const orderOk = ['asc', 'desc'];

    const filterSubscriptionRaw = p.filterSubscription === '' ? 'all' : p.filterSubscription;

    return {
      searchTerm: typeof p.searchTerm === 'string' ? p.searchTerm : ADMIN_UI_DEFAULTS.searchTerm,
      filterPulse: pulseOk.includes(p.filterPulse) ? p.filterPulse : ADMIN_UI_DEFAULTS.filterPulse,
      filterMealPlan: mealOk.includes(p.filterMealPlan) ? p.filterMealPlan : ADMIN_UI_DEFAULTS.filterMealPlan,
      filterSubscription: subOk.includes(filterSubscriptionRaw) ? filterSubscriptionRaw : ADMIN_UI_DEFAULTS.filterSubscription,
      filterWorkoutPlan: workoutOk.includes(p.filterWorkoutPlan) ? p.filterWorkoutPlan : ADMIN_UI_DEFAULTS.filterWorkoutPlan,
      filterWeight: weightOk.includes(p.filterWeight) ? p.filterWeight : ADMIN_UI_DEFAULTS.filterWeight,
      onboardingFilter: onboardOk.includes(p.onboardingFilter) ? p.onboardingFilter : ADMIN_UI_DEFAULTS.onboardingFilter,
      sortBy: sortByOk.includes(p.sortBy) ? p.sortBy : ADMIN_UI_DEFAULTS.sortBy,
      sortOrder: orderOk.includes(p.sortOrder) ? p.sortOrder : ADMIN_UI_DEFAULTS.sortOrder,
    };
  } catch {
    return { ...ADMIN_UI_DEFAULTS };
  }
}

function writeAdminUiPrefs(prefs) {
  try {
    sessionStorage.setItem(ADMIN_DASHBOARD_UI_SESSION_KEY, JSON.stringify(prefs));
  } catch (e) {
    console.warn('Admin dashboard: could not write UI prefs to session', e);
  }
}

function AdminDashboardPage() {
  const { isDarkMode, themeClasses } = useTheme();
  const { setSelectedClient } = useClient();
  const navigate = useNavigate();

  // Visual tokens from DESIGN.md. Primary action: find a client who needs attention.
  const inputBase = `w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-md px-3 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-forest focus:border-forest ${themeClasses.textPrimary} transition-colors duration-120 placeholder-ink-muted dark:placeholder-ink-muted-dark min-h-11`;
  const btnPrimary = `min-h-11 px-4 py-2.5 rounded-md font-semibold bg-forest-ondark text-white hover:brightness-95 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover dark:hover:brightness-100 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none transition-colors duration-120`;
  const btnSecondary = `min-h-11 px-4 py-2.5 rounded-md font-medium border border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-120`;
  const cardBase = `${themeClasses.bgCard} rounded-lg border border-line dark:border-line-dark transition-colors duration-120`;

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'english';
  });
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  // --- DATA STATE ---
  const [clients, setClients] = useState([]);
  const [kpis, setKpis] = useState({
    activeEngagement: 0,
    quietZone: 0,
    silentZone: 0,
    expectedIncomeThisMonth: 0,
    usdToIlsRate: 3.7,
    exchangeRateSource: 'fallback',
  });
  /** Matches server onboardingStepTotal (profile steps + subscription gate; progress bar denominator). */
  const [onboardingStepTotal, setOnboardingStepTotal] = useState(16);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null); // when dashboard data was last fetched
  const adminUiInitRef = useRef(null);
  if (adminUiInitRef.current === null) {
    adminUiInitRef.current = readAdminUiPrefs();
  }
  const uiInit = adminUiInitRef.current;
  const [searchTerm, setSearchTerm] = useState(uiInit.searchTerm);
  const [filterPulse, setFilterPulse] = useState(uiInit.filterPulse);
  const [filterMealPlan, setFilterMealPlan] = useState(uiInit.filterMealPlan);
  const [filterSubscription, setFilterSubscription] = useState(uiInit.filterSubscription);
  const [filterWorkoutPlan, setFilterWorkoutPlan] = useState(uiInit.filterWorkoutPlan);
  const [filterWeight, setFilterWeight] = useState(uiInit.filterWeight);
  const [onboardingFilter, setOnboardingFilter] = useState(uiInit.onboardingFilter); // 'all' | 'incomplete'
  /** Single filter button + drill-down panel; null = category list */
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterPanelCategory, setFilterPanelCategory] = useState(null);
  const filterPanelRef = useRef(null);
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [sortPanelColumn, setSortPanelColumn] = useState(null);
  const sortPanelRef = useRef(null);
  const [sortBy, setSortBy] = useState(uiInit.sortBy); // '' | 'name' | 'mealPlanStatus' | 'statusUpdated' | 'weight' | 'workoutPlan' | 'payment' | 'subscription' | 'engagementPulse'
  const [sortOrder, setSortOrder] = useState(uiInit.sortOrder); // 'asc' | 'desc'
  const [expectedIncomeCurrency, setExpectedIncomeCurrency] = useState('ILS'); // 'ILS' | 'USD'
  const [tableLimit, setTableLimit] = useState(10);
  const [receiptsModalOpen, setReceiptsModalOpen] = useState(false);
  const [, setReceiptsModalCustomerId] = useState(null);
  const [receiptsModalLoading, setReceiptsModalLoading] = useState(false);
  const [receiptsModalData, setReceiptsModalData] = useState(null);
  const [helpTooltipActive, setHelpTooltipActive] = useState(null); // contentKey when open
  const [helpTooltipRect, setHelpTooltipRect] = useState(null);
  const helpTooltipTriggerRef = useRef(null);
  const [showClientAssignmentsColumn, setShowClientAssignmentsColumn] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  /** From GET /api/admin/dashboard — drives sys_admin referral company picker. */
  const [userRole, setUserRole] = useState(null);
  const [assignableProviders, setAssignableProviders] = useState([]);
  const [updatingProviderFor, setUpdatingProviderFor] = useState({}); // user_code -> true while updating
  const [updatingBlockFor, setUpdatingBlockFor] = useState({}); // user_code -> true while updating block status
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [openKebabId, setOpenKebabId] = useState(null);
  const [chatTooltipClientId, setChatTooltipClientId] = useState(null);
  // The client open in the quick-look panel.
  const [peekClientId, setPeekClientId] = useState(null);
  const [chatTooltipRect, setChatTooltipRect] = useState(null);
  const chatTooltipTriggerRef = useRef(null);

  useEffect(() => {
    writeAdminUiPrefs({
      searchTerm,
      filterPulse,
      filterMealPlan,
      filterSubscription,
      filterWorkoutPlan,
      filterWeight,
      onboardingFilter,
      sortBy,
      sortOrder,
    });
  }, [
    searchTerm,
    filterPulse,
    filterMealPlan,
    filterSubscription,
    filterWorkoutPlan,
    filterWeight,
    onboardingFilter,
    sortBy,
    sortOrder,
  ]);

  // --- REFERRAL LINK STATE ---
  const [referralLinkDialogOpen, setReferralLinkDialogOpen] = useState(false);
  const qrCodeRef = useRef(null);
  const limitedQrCodeRefs = useRef({});
  const [linkType, setLinkType] = useState('simple');
  const [maxClients, setMaxClients] = useState(30);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('');
  const [, setLimitedLinks] = useState([]);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [registrationLinks, setRegistrationLinks] = useState([]);
  const [registrationLinksLoading, setRegistrationLinksLoading] = useState(false);
  const [activeLinkCarouselIndex, setActiveLinkCarouselIndex] = useState(0);
  const [deactivatingLinkId, setDeactivatingLinkId] = useState(null);
  const registrationLinksScrollRef = useRef(null);
  const [referralOnboardingCode, setReferralOnboardingCode] = useState('');
  const [referralOnboardingMaxUses, setReferralOnboardingMaxUses] = useState(null);
  const [onboardingCodeSlotCount, setOnboardingCodeSlotCount] = useState(1);
  const [onboardingCodeUnlimitedSlots, setOnboardingCodeUnlimitedSlots] = useState(false);
  const [generatingOnboardingCode, setGeneratingOnboardingCode] = useState(false);
  const [onboardingCodesHistory, setOnboardingCodesHistory] = useState([]);
  const [onboardingCodesHistoryLoading, setOnboardingCodesHistoryLoading] = useState(false);
  const [onboardingCodesShowInactive, setOnboardingCodesShowInactive] = useState(false);
  const [deactivatingOnboardingId, setDeactivatingOnboardingId] = useState(null);
  const [limitedSkipPricing, setLimitedSkipPricing] = useState(false);
  /** Sys admin: optional company → referral uses first company_manager id from API. */
  const [referralCompanies, setReferralCompanies] = useState([]);
  const [referralCompaniesLoading, setReferralCompaniesLoading] = useState(false);
  const [registrationLinksShowInactive, setRegistrationLinksShowInactive] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingLinkMaxClients, setEditingLinkMaxClients] = useState(30);
  const [editingLinkSkipPricing, setEditingLinkSkipPricing] = useState(false);
  const [editingLinkSaving, setEditingLinkSaving] = useState(false);
  const [referralTargetCompanyId, setReferralTargetCompanyId] = useState('');
  const [referralCompanyManagerId, setReferralCompanyManagerId] = useState(null);
  const [referralManagerLoading, setReferralManagerLoading] = useState(false);
  const [referralManagerError, setReferralManagerError] = useState(null);
  const [referralCompanyMenuOpen, setReferralCompanyMenuOpen] = useState(false);
  const referralCompanyMenuRef = useRef(null);

  // --- FETCH DATA (API returns only clients for current user's role) ---
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await authClient.getAccessToken();
      if (!token) {
        setError('Authorization required');
        setLoading(false);
        return;
      }
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const url = `${base}/admin/dashboard`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 401) {
        await authClient.signOut();
        setError('Session expired. Please sign in again.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        if (response.status === 403 && errBody.code === 'PROFILE_NOT_FOUND') {
          setError(errBody.error || 'User profile not found.');
          setLoading(false);
          return;
        }
        throw new Error(errBody.error || 'Failed to load dashboard data');
      }

      const data = await response.json();
      const rawClients = data.clients || [];
      const clientsList = withTourDemoClient(rawClients);
      const kpisData = data.kpis || { activeEngagement: 0, quietZone: 0, silentZone: 0, expectedIncomeThisMonth: 0, usdToIlsRate: 3.7, exchangeRateSource: 'fallback' };
      const stepTotal = typeof data.onboardingStepTotal === 'number' && data.onboardingStepTotal > 0 ? data.onboardingStepTotal : 20;
      const now = new Date();
      const showAssignments = !!data.showClientAssignmentsColumn;
      const company = data.companyName ?? null;
      const providers = Array.isArray(data.assignableProviders) ? data.assignableProviders : [];

      setClients(clientsList);
      setKpis(kpisData);
      setOnboardingStepTotal(stepTotal);
      setLastUpdated(now);
      setShowClientAssignmentsColumn(showAssignments);
      setCompanyName(company);
      setUserRole(data.userRole ?? null);
      setAssignableProviders(providers);

      dashboardCache.clients = clientsList;
      dashboardCache.kpis = kpisData;
      dashboardCache.lastUpdated = now;
      dashboardCache.showClientAssignmentsColumn = showAssignments;
      dashboardCache.companyName = company;
      dashboardCache.assignableProviders = providers;
      dashboardCache.onboardingStepTotal = stepTotal;
      dashboardCache.userRole = data.userRole ?? null;
      setTopbarClientsCache(clientsList);
      writeSessionCache({
        clients: rawClients,
        kpis: kpisData,
        lastUpdated: now,
        showClientAssignmentsColumn: showAssignments,
        companyName: company,
        assignableProviders: providers,
        onboardingStepTotal: stepTotal,
        userRole: data.userRole ?? null,
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const applyCache = (source) => {
      setClients(withTourDemoClient(source.clients || []));
      setKpis(source.kpis || dashboardCache.kpis);
      setLastUpdated(source.lastUpdated ?? null);
      setShowClientAssignmentsColumn(!!source.showClientAssignmentsColumn);
      setCompanyName(source.companyName ?? null);
      setUserRole(source.userRole ?? null);
      setAssignableProviders(Array.isArray(source.assignableProviders) ? source.assignableProviders : []);
      setOnboardingStepTotal(
        typeof source.onboardingStepTotal === 'number' && source.onboardingStepTotal > 0
          ? source.onboardingStepTotal
          : 20
      );
      setTopbarClientsCache(withTourDemoClient(source.clients || []));
      setLoading(false);
    };

    const session = readSessionCache();
    if (session && isCacheFresh(session.lastUpdated)) {
      dashboardCache.clients = session.clients;
      dashboardCache.kpis = session.kpis;
      dashboardCache.lastUpdated = session.lastUpdated;
      dashboardCache.showClientAssignmentsColumn = session.showClientAssignmentsColumn;
      dashboardCache.companyName = session.companyName;
      dashboardCache.assignableProviders = session.assignableProviders || [];
      dashboardCache.onboardingStepTotal =
        typeof session.onboardingStepTotal === 'number' && session.onboardingStepTotal > 0
          ? session.onboardingStepTotal
          : 20;
      dashboardCache.userRole = session.userRole ?? null;
      applyCache(session);
      return;
    }

    const hasMemoryCache = dashboardCache.lastUpdated != null && (dashboardCache.clients.length > 0 || dashboardCache.kpis?.exchangeRateSource != null);
    if (hasMemoryCache && isCacheFresh(dashboardCache.lastUpdated)) {
      applyCache(dashboardCache);
      return;
    }

    loadDashboardData();
  }, []);

  useEffect(() => {
    if (loading) return;
    window.dispatchEvent(new CustomEvent('admin-dashboard-ready'));
  }, [loading]);

  // Inject / remove the tour example client when playbook demo mode toggles.
  useEffect(() => {
    const syncTourDemo = () => {
      setClients((prev) => withTourDemoClient(prev));
    };
    if (isTourDemoActive()) syncTourDemo();
    window.addEventListener('tour-demo-changed', syncTourDemo);
    return () => window.removeEventListener('tour-demo-changed', syncTourDemo);
  }, []);

  // Help tooltip: track rect so portal can position over table / KPI cards
  useEffect(() => {
    if (!helpTooltipActive) {
      setHelpTooltipRect(null);
      return;
    }
    const updateRect = () => {
      if (helpTooltipTriggerRef.current) {
        setHelpTooltipRect(helpTooltipTriggerRef.current.getBoundingClientRect());
      }
    };
    const raf = requestAnimationFrame(() => updateRect());
    const handleScrollOrResize = () => updateRect();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [helpTooltipActive]);

  useEffect(() => {
    if (!helpTooltipActive) return;
    const handleClickOutside = (e) => {
      const trigger = helpTooltipTriggerRef.current;
      const portal = document.getElementById('help-tooltip-portal');
      if (trigger && !trigger.contains(e.target) && portal && !portal.contains(e.target)) {
        setHelpTooltipActive(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [helpTooltipActive]);

  useEffect(() => {
    if (!openKebabId) return;
    const handleClickOutside = () => setOpenKebabId(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openKebabId]);

  // Chat preview tooltip: track trigger rect so portal can position over table and open up/down
  useEffect(() => {
    if (!chatTooltipClientId) {
      setChatTooltipRect(null);
      return;
    }
    const updateRect = () => {
      if (chatTooltipTriggerRef.current) {
        setChatTooltipRect(chatTooltipTriggerRef.current.getBoundingClientRect());
      }
    };
    const raf = requestAnimationFrame(() => updateRect());
    const handleScrollOrResize = () => updateRect();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [chatTooltipClientId]);

  const openReceiptsModal = async (stripeCustomerId) => {
    if (!stripeCustomerId) return;
    setReceiptsModalCustomerId(stripeCustomerId);
    setReceiptsModalOpen(true);
    setReceiptsModalData(null);
    setReceiptsModalLoading(true);
    try {
      const token = await authClient.getAccessToken();
      if (!token) return;
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const res = await fetch(`${base}/admin/customer-receipts?stripe_customer_id=${encodeURIComponent(stripeCustomerId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setReceiptsModalData(data);
      else setReceiptsModalData({ error: data.error || 'Failed to load receipts' });
    } catch (err) {
      setReceiptsModalData({ error: err.message || 'Failed to load receipts' });
    } finally {
      setReceiptsModalLoading(false);
    }
  };

  const closeReceiptsModal = () => {
    setReceiptsModalOpen(false);
    setReceiptsModalCustomerId(null);
    setReceiptsModalData(null);
  };

  const handleDeleteClientClick = (e, client) => {
    e.stopPropagation();
    setClientToDelete(client);
  };

  const handleDeleteClientConfirm = async () => {
    const required = language === 'hebrew' ? 'מחק' : 'DELETE';
    if (!clientToDelete || deleteConfirmText.trim() !== required) return;
    let alreadyGone = false;
    try {
      setIsDeleting(true);
      try {
        await deleteClient(clientToDelete.id);
      } catch (err) {
        // The list can be up to 10 minutes old (session cache), so the client may have been
        // deleted elsewhere already. That is the outcome the user wanted: drop the row.
        if (err.status !== 404) throw err;
        alreadyGone = true;
      }
      const updated = clients.filter((c) => c.id !== clientToDelete.id);
      setClients(updated);
      dashboardCache.clients = updated;
      setTopbarClientsCache(updated);
      writeSessionCache({
        clients: updated,
        kpis: dashboardCache.kpis,
        lastUpdated: dashboardCache.lastUpdated,
        showClientAssignmentsColumn: dashboardCache.showClientAssignmentsColumn,
        companyName: dashboardCache.companyName,
        assignableProviders: dashboardCache.assignableProviders || [],
        onboardingStepTotal: dashboardCache.onboardingStepTotal ?? 16,
        userRole: dashboardCache.userRole ?? null,
      });
      setClientToDelete(null);
      setDeleteConfirmText('');
      if (alreadyGone) {
        alert(language === 'hebrew' ? 'הלקוח כבר נמחק קודם לכן. הוא הוסר מהרשימה.' : 'This client was already deleted. Removed from the list.');
      } else {
        alert(language === 'hebrew' ? 'הלקוח נמחק בהצלחה' : 'Client deleted successfully');
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      alert(language === 'hebrew' ? `שגיאה במחיקת לקוח: ${err.message}` : `Error deleting client: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setClientToDelete(null);
    setDeleteConfirmText('');
  };

  // Resolve current user id when referral dialog opens
  useEffect(() => {
    if (!referralLinkDialogOpen) return;
    let cancelled = false;
    (async () => {
      const session = await authClient.getSession();
      if (!cancelled && session?.user?.id) setCurrentUserId(session.user.id);
    })();
    return () => { cancelled = true; };
  }, [referralLinkDialogOpen]);

  const effectiveReferralManagerId = useMemo(() => {
    if (!currentUserId) return null;
    if (userRole !== 'sys_admin') return currentUserId;
    if (!referralTargetCompanyId) return currentUserId;
    return referralCompanyManagerId || null;
  }, [currentUserId, userRole, referralTargetCompanyId, referralCompanyManagerId]);

  useEffect(() => {
    if (!referralLinkDialogOpen || userRole !== 'sys_admin') return;
    let cancelled = false;
    (async () => {
      setReferralCompaniesLoading(true);
      try {
        const token = await authClient.getAccessToken();
        if (!token || cancelled) return;
        const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
        const res = await fetch(`${base}/companies`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to load companies');
        const list = await res.json();
        if (!cancelled) setReferralCompanies(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setReferralCompanies([]);
      } finally {
        if (!cancelled) setReferralCompaniesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [referralLinkDialogOpen, userRole]);

  useEffect(() => {
    if (!referralLinkDialogOpen || userRole !== 'sys_admin') return;
    if (!referralTargetCompanyId) {
      setReferralCompanyManagerId(null);
      setReferralManagerError(null);
      setReferralManagerLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setReferralManagerLoading(true);
      setReferralManagerError(null);
      setReferralCompanyManagerId(null);
      try {
        const token = await authClient.getAccessToken();
        if (!token) return;
        const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
        const res = await fetch(
          `${base}/admin/companies/${encodeURIComponent(referralTargetCompanyId)}/referral-manager-id`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Failed to resolve company manager');
        }
        if (!cancelled && data.manager_id) {
          setReferralCompanyManagerId(String(data.manager_id));
        }
      } catch (e) {
        if (!cancelled) {
          setReferralManagerError(e.message || 'Error');
          setReferralCompanyManagerId(null);
        }
      } finally {
        if (!cancelled) setReferralManagerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [referralLinkDialogOpen, userRole, referralTargetCompanyId]);

  useEffect(() => {
    if (!referralLinkDialogOpen) setReferralCompanyMenuOpen(false);
  }, [referralLinkDialogOpen]);

  useEffect(() => {
    if (!referralCompanyMenuOpen) return;
    const onPointerDown = (e) => {
      const el = referralCompanyMenuRef.current;
      if (el && !el.contains(e.target)) setReferralCompanyMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [referralCompanyMenuOpen]);

  useEffect(() => {
    if (!referralLinkDialogOpen) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (referralCompanyMenuOpen) {
        setReferralCompanyMenuOpen(false);
        return;
      }
      setReferralLinkDialogOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [referralLinkDialogOpen, referralCompanyMenuOpen]);

  const buildLimitedLinkUrl = (row) => {
    const payload = row.skip_pricing ? { link_id: row.link_id, sp: 1 } : { link_id: row.link_id };
    return `https://betterchoice.one/landing#d=${btoa(JSON.stringify(payload))}`;
  };

  const generateClientReferralLink = () => {
    const mid = effectiveReferralManagerId;
    if (!mid) return '';
    return `https://betterchoice.one/landing#d=${btoa(mid)}`;
  };

  const fetchRegistrationLinks = async () => {
    try {
      setRegistrationLinksLoading(true);
      const token = await authClient.getAccessToken();
      if (!token) return;
      const registrationBase = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const q = registrationLinksShowInactive 
        ? 'active_only=false' 
        : `active_only=true&before_date=${encodeURIComponent(today.toISOString())}`;
      const res = await fetch(`${registrationBase}/db/registration-links?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load links');
      const data = await res.json();
      setRegistrationLinks(Array.isArray(data) ? data : []);
      setActiveLinkCarouselIndex(0);
    } catch (err) {
      console.error('Error fetching registration links:', err);
      setRegistrationLinks([]);
    } finally {
      setRegistrationLinksLoading(false);
    }
  };

  useEffect(() => {
    if (referralLinkDialogOpen && linkType === 'limited') {
      fetchRegistrationLinks();
    }
  }, [referralLinkDialogOpen, linkType, registrationLinksShowInactive]);

  const fetchOnboardingCodesHistory = async () => {
    try {
      setOnboardingCodesHistoryLoading(true);
      const token = await authClient.getAccessToken();
      if (!token) return;
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const q = onboardingCodesShowInactive ? 'active_only=false' : 'active_only=true';
      const res = await fetch(`${base}/admin/onboarding-access-codes?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load codes');
      const data = await res.json();
      setOnboardingCodesHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching onboarding codes:', err);
      setOnboardingCodesHistory([]);
    } finally {
      setOnboardingCodesHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (referralLinkDialogOpen && linkType === 'onboarding') {
      fetchOnboardingCodesHistory();
    }
  }, [referralLinkDialogOpen, linkType, onboardingCodesShowInactive]);

  const activeRegistrationLinks = useMemo(
    () => registrationLinks.filter((r) => r.is_active !== false && (!r.expires_at || new Date(r.expires_at) >= new Date(new Date().setHours(0, 0, 0, 0)))),
    [registrationLinks]
  );

  useEffect(() => {
    if (activeRegistrationLinks.length > 0 && activeLinkCarouselIndex >= activeRegistrationLinks.length) {
      setActiveLinkCarouselIndex(activeRegistrationLinks.length - 1);
    }
  }, [activeRegistrationLinks.length, activeLinkCarouselIndex]);

  const goToSlide = (index) => {
    const el = registrationLinksScrollRef.current;
    if (!el) return;
    const target = Math.max(0, Math.min(index, activeRegistrationLinks.length - 1));
    setActiveLinkCarouselIndex(target);
    el.scrollTo({ left: target * el.clientWidth, behavior: 'smooth' });
  };

  const handleGenerateOnboardingAccessCode = async () => {
    try {
      setGeneratingOnboardingCode(true);
      const token = await authClient.getAccessToken();
      if (!token) {
        alert(language === 'hebrew' ? 'נדרשת התחברות' : 'Sign in required');
        return;
      }
      const n = parseInt(String(onboardingCodeSlotCount), 10);
      const slots = Number.isFinite(n) && n >= 1 ? Math.min(n, 100000) : 1;
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const companyExtra =
        userRole === 'sys_admin' && referralTargetCompanyId
          ? { company_id: referralTargetCompanyId }
          : {};
      const res = await fetch(`${base}/admin/onboarding-access-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(
          onboardingCodeUnlimitedSlots
            ? { unlimited: true, ...companyExtra }
            : { max_uses: slots, ...companyExtra }
        ),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || (language === 'hebrew' ? 'יצירת הקוד נכשלה' : 'Failed to create code'));
      }
      if (data.code) {
        setReferralOnboardingCode(String(data.code));
        setReferralOnboardingMaxUses(data.max_uses != null ? data.max_uses : null);
      }
      await fetchOnboardingCodesHistory();
    } catch (err) {
      console.error(err);
      alert(language === 'hebrew' ? `שגיאה: ${err.message}` : `Error: ${err.message}`);
    } finally {
      setGeneratingOnboardingCode(false);
    }
  };

  const handleGenerateLimitedLink = async () => {
    if (!effectiveReferralManagerId || !expiryDate) {
      alert(
        language === 'hebrew'
          ? 'נדרש תאריך תפוגה ומזהה מנהל תקף'
          : 'Expiry date is required and a valid manager must be selected'
      );
      return;
    }
    try {
      setGeneratingLink(true);
      let expiryDateTime = null;
      if (expiryDate && expiryTime) {
        expiryDateTime = new Date(`${expiryDate}T${expiryTime}:00`).toISOString();
      } else if (expiryDate) {
        expiryDateTime = new Date(`${expiryDate}T23:59:59`).toISOString();
      }
      const linkId = crypto.randomUUID?.() || `link-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const companyExtra =
        userRole === 'sys_admin' && referralTargetCompanyId
          ? { company_id: referralTargetCompanyId }
          : {};
      const linkData = {
        link_id: linkId,
        manager_id: effectiveReferralManagerId,
        max_clients: parseInt(maxClients) || 30,
        ...(expiryDateTime && { expiry_date: expiryDateTime }),
        skip_pricing: limitedSkipPricing,
        ...companyExtra,
      };
      const registrationBase = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const token = await authClient.getAccessToken();
      const res = await fetch(`${registrationBase}/db/registration-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(linkData),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save link');
      }
      const created = await res.json();
      const newRow = {
        id: created.id,
        link_id: created.link_id ?? linkId,
        manager_id: created.manager_id ?? effectiveReferralManagerId,
        max_slots: created.max_clients ?? linkData.max_clients,
        max_clients: created.max_clients ?? linkData.max_clients,
        current_count: created.current_count ?? 0,
        expires_at: created.expiry_date ?? expiryDateTime,
        expiry_date: created.expiry_date ?? expiryDateTime,
        is_active: created.is_active ?? true,
        created_at: created.created_at ?? new Date().toISOString(),
        skip_pricing: created.skip_pricing ?? limitedSkipPricing,
      };
      const landingUrl = buildLimitedLinkUrl(newRow);
      setRegistrationLinks(prev => [newRow, ...prev]);
      setLimitedLinks(prev => [...prev, { id: linkId, link: landingUrl, max_clients: linkData.max_clients, expiry_date: expiryDateTime, created_at: new Date().toISOString() }]);
      setExpiryDate('');
      setExpiryTime('');
      setMaxClients(30);
    } catch (err) {
      console.error('Error generating limited link:', err);
      alert(language === 'hebrew' ? `שגיאה: ${err.message}` : `Error: ${err.message}`);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyReferralLink = async (linkUrl = null) => {
    const link = linkUrl ?? (linkType === 'limited' ? '' : generateClientReferralLink());
    if (!link) {
      alert(language === 'hebrew' ? 'לא ניתן להעתיק קישור ריק' : 'Cannot copy empty link');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      alert(t('admin.referralLinkCopied'));
    } catch (err) {
      console.error('Error copying link:', err);
      alert(language === 'hebrew' ? 'שגיאה בהעתקת קישור' : 'Error copying link');
    }
  };

  const handleCopyOnboardingCodeString = async (codeRaw) => {
    const code = String(codeRaw || '').trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      alert(t('admin.referral.codeCopied'));
    } catch (err) {
      console.error('Error copying code:', err);
      alert(language === 'hebrew' ? 'שגיאה בהעתקת הקוד' : 'Error copying code');
    }
  };

  const handleCopyOnboardingCode = async () => {
    await handleCopyOnboardingCodeString(referralOnboardingCode);
  };

  const handleCopyQRCodeImage = async (linkId = null) => {
    try {
      const qrContainer =
        linkType === 'limited' && linkId != null
          ? limitedQrCodeRefs.current?.[linkId]
          : qrCodeRef.current;
      if (!qrContainer) {
        alert(language === 'hebrew' ? 'QR code לא נמצא' : 'QR code not found');
        return;
      }
      const svgElement = qrContainer.querySelector('svg');
      if (!svgElement) {
        alert(language === 'hebrew' ? 'QR code לא נמצא' : 'QR code not found');
        return;
      }
      const clonedSvg = svgElement.cloneNode(true);
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const svgData = new XMLSerializer().serializeToString(clonedSvg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const scale = Math.max(window.devicePixelRatio || 2, 2);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 800;
        canvas.width = size * scale;
        canvas.height = size * scale;
        const ctx = canvas.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, '#34C759');
        gradient.addColorStop(1, '#34C759');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${48 * scale}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText('BetterChoice AI', canvas.width / 2, 80 * scale);
        ctx.font = `bold ${32 * scale}px Arial`;
        ctx.fillText(language === 'hebrew' ? 'הירשם' : 'Sign Up', canvas.width / 2, 140 * scale);
        ctx.font = `${20 * scale}px Arial`;
        ctx.fillText(language === 'hebrew' ? 'סרוק כדי להתחיל' : 'Scan to get started', canvas.width / 2, 180 * scale);
        const qrSize = 400 * scale;
        const qrX = (canvas.width - qrSize) / 2;
        const qrY = 220 * scale;
        ctx.fillStyle = '#f0fdf4';
        const radius = 20 * scale;
        ctx.beginPath();
        ctx.moveTo(qrX + radius, qrY);
        ctx.lineTo(qrX + qrSize - radius, qrY);
        ctx.quadraticCurveTo(qrX + qrSize, qrY, qrX + qrSize, qrY + radius);
        ctx.lineTo(qrX + qrSize, qrY + qrSize - radius);
        ctx.quadraticCurveTo(qrX + qrSize, qrY + qrSize, qrX + qrSize - radius, qrY + qrSize);
        ctx.lineTo(qrX + radius, qrY + qrSize);
        ctx.quadraticCurveTo(qrX, qrY + qrSize, qrX, qrY + qrSize - radius);
        ctx.lineTo(qrX, qrY + radius);
        ctx.quadraticCurveTo(qrX, qrY, qrX + radius, qrY);
        ctx.closePath();
        ctx.fill();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
        ctx.shadowBlur = 20 * scale;
        ctx.shadowOffsetY = 10 * scale;
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.drawImage(img, qrX + 20 * scale, qrY + 20 * scale, qrSize - 40 * scale, qrSize - 40 * scale);
        ctx.fillStyle = '#ffffff';
        ctx.font = `${16 * scale}px Arial`;
        ctx.fillText('betterchoice.one', canvas.width / 2, canvas.height - 40 * scale);
        canvas.toBlob(async (blob) => {
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            alert(language === 'hebrew' ? 'QR code הועתק ללוח' : 'QR code copied to clipboard');
          } catch (e) {
            console.error(e);
          }
          URL.revokeObjectURL(svgUrl);
        }, 'image/png', 1.0);
      };
      img.src = svgUrl;
    } catch (err) {
      console.error('Error copying QR code:', err);
      alert(language === 'hebrew' ? 'שגיאה בהעתקת QR code' : 'Error copying QR code');
    }
  };

  const handleDeactivateOnboardingCode = async (rowId) => {
    if (!rowId || deactivatingOnboardingId) return;
    if (!window.confirm(
      language === 'hebrew'
        ? 'לבטל קוד גישה זה? לקוחות לא יוכלו עוד להשתמש בו להרשמות חדשות.'
        : 'Deactivate this access code? Clients will no longer be able to use it for new signups.'
    )) return;
    try {
      setDeactivatingOnboardingId(rowId);
      const token = await authClient.getAccessToken();
      if (!token) return;
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const res = await fetch(`${base}/admin/onboarding-access-codes/${encodeURIComponent(rowId)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to deactivate');
      }
      await fetchOnboardingCodesHistory();
    } catch (err) {
      console.error('Error deactivating onboarding code:', err);
      alert(language === 'hebrew' ? `שגיאה: ${err.message}` : `Error: ${err.message}`);
    } finally {
      setDeactivatingOnboardingId(null);
    }
  };

  const handleDeactivateLink = async (linkId) => {
    if (!linkId || deactivatingLinkId) return;
    if (!window.confirm(language === 'hebrew' ? 'לבטל קישור זה? הוא לא יהיה פעיל להרשמות חדשות.' : 'Deactivate this link? It will no longer accept new signups.')) return;
    try {
      setDeactivatingLinkId(linkId);
      const token = await authClient.getAccessToken();
      if (!token) return;
      const registrationBase = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const res = await fetch(`${registrationBase}/db/registration-links/${encodeURIComponent(linkId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: false })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to deactivate');
      }
      setRegistrationLinks(prev => prev.map(r => (r.link_id === linkId ? { ...r, is_active: false } : r)));
    } catch (err) {
      console.error('Error deactivating link:', err);
      alert(language === 'hebrew' ? `שגיאה: ${err.message}` : `Error: ${err.message}`);
    } finally {
      setDeactivatingLinkId(null);
    }
  };

  const handleSaveEditedLink = async (linkId) => {
    try {
      setEditingLinkSaving(true);
      const token = await authClient.getAccessToken();
      const registrationBase = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const res = await fetch(`${registrationBase}/db/registration-links/${encodeURIComponent(linkId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ 
          max_slots: parseInt(editingLinkMaxClients) || 30,
          skip_pricing: editingLinkSkipPricing
        })
      });
      if (!res.ok) throw new Error('Failed to update link');
      const updated = await res.json();
      setRegistrationLinks(prev => prev.map(r => (r.link_id === linkId ? { 
        ...r, 
        max_slots: updated.max_slots, 
        max_clients: updated.max_clients, 
        skip_pricing: updated.skip_pricing 
      } : r)));
      setEditingLinkId(null);
    } catch (err) {
      console.error('Error updating link:', err);
      alert(language === 'hebrew' ? 'שגיאה בעדכון הקישור' : 'Error updating link');
    } finally {
      setEditingLinkSaving(false);
    }
  };

  useEffect(() => {
    if (linkType !== 'limited') setLimitedLinks([]);
  }, [linkType]);

  useEffect(() => {
    if (!referralLinkDialogOpen) {
      setLinkType('simple');
      setReferralOnboardingCode('');
      setReferralOnboardingMaxUses(null);
      setOnboardingCodeSlotCount(1);
      setOnboardingCodeUnlimitedSlots(false);
      setOnboardingCodesHistory([]);
      setOnboardingCodesShowInactive(false);
      setDeactivatingOnboardingId(null);
      setReferralTargetCompanyId('');
      setReferralCompanyManagerId(null);
      setReferralManagerError(null);
      setReferralManagerLoading(false);
      setReferralCompanies([]);
    }
  }, [referralLinkDialogOpen]);

  // Guided tour: open/close the referral dialog without relying on toggles.
  useEffect(() => {
    const open = () => setReferralLinkDialogOpen(true);
    const close = () => setReferralLinkDialogOpen(false);
    window.addEventListener('portfolio-playbook-open-referral', open);
    window.addEventListener('portfolio-playbook-close-referral', close);
    return () => {
      window.removeEventListener('portfolio-playbook-open-referral', open);
      window.removeEventListener('portfolio-playbook-close-referral', close);
    };
  }, []);

  // Guided tour: clear saved filters so the tour client is in the list, and open its quick look.
  useEffect(() => {
    const reset = () => {
      setSearchTerm(ADMIN_UI_DEFAULTS.searchTerm);
      setFilterPulse(ADMIN_UI_DEFAULTS.filterPulse);
      setFilterMealPlan(ADMIN_UI_DEFAULTS.filterMealPlan);
      setFilterSubscription(ADMIN_UI_DEFAULTS.filterSubscription);
      setFilterWorkoutPlan(ADMIN_UI_DEFAULTS.filterWorkoutPlan);
      setFilterWeight(ADMIN_UI_DEFAULTS.filterWeight);
      setOnboardingFilter(ADMIN_UI_DEFAULTS.onboardingFilter);
      setSortBy(ADMIN_UI_DEFAULTS.sortBy);
      setSortOrder(ADMIN_UI_DEFAULTS.sortOrder);
      setTableLimit(10);
    };
    const openPeekForTour = () => setPeekClientId(DEMO_CLIENT_ID);
    const closePeekForTour = () => setPeekClientId(null);
    window.addEventListener('portfolio-playbook-reset-dashboard-filters', reset);
    window.addEventListener('portfolio-playbook-open-peek', openPeekForTour);
    window.addEventListener('portfolio-playbook-close-peek', closePeekForTour);
    return () => {
      window.removeEventListener('portfolio-playbook-reset-dashboard-filters', reset);
      window.removeEventListener('portfolio-playbook-open-peek', openPeekForTour);
      window.removeEventListener('portfolio-playbook-close-peek', closePeekForTour);
    };
  }, []);

  const handleAssignProvider = async (userCode, providerId) => {
    if (isTourDemoActive() && userCode === DEMO_USER_CODE) return;
    const value = providerId || null;
    try {
      setUpdatingProviderFor(prev => ({ ...prev, [userCode]: true }));
      const token = await authClient.getAccessToken();
      if (!token) {
        setError('Authorization required');
        return;
      }
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const res = await fetch(`${base}/chat-users/${encodeURIComponent(userCode)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ provider_id: value }),
      });
      const errBody = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errBody.error || 'Failed to update');
      const updatedProviderName = value && assignableProviders.length
        ? (assignableProviders.find(p => p.id === value)?.name ?? null)
        : null;
      setClients(prev => prev.map(c =>
        c.user_code === userCode ? { ...c, provider_id: value, provider_name: updatedProviderName } : c
      ));
      dashboardCache.clients = dashboardCache.clients.map(c =>
        c.user_code === userCode ? { ...c, provider_id: value, provider_name: updatedProviderName } : c
      );
    } catch (err) {
      console.error('Error assigning provider:', err);
      alert(err.message || 'Failed to update managing dietitian');
    } finally {
      setUpdatingProviderFor(prev => ({ ...prev, [userCode]: false }));
    }
  };

  const handleToggleClientBlock = async (client) => {
    if (!client?.user_code) return;
    const nextBlocked = !client.is_blocked;
    try {
      setUpdatingBlockFor(prev => ({ ...prev, [client.user_code]: true }));
      const token = await authClient.getAccessToken();
      if (!token) {
        setError('Authorization required');
        return;
      }
      const base = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      const res = await fetch(`${base}/chat-users/${encodeURIComponent(client.user_code)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_blocked: nextBlocked }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to update block status');

      setClients(prev => prev.map(c =>
        c.user_code === client.user_code ? { ...c, is_blocked: nextBlocked } : c
      ));
      dashboardCache.clients = dashboardCache.clients.map(c =>
        c.user_code === client.user_code ? { ...c, is_blocked: nextBlocked } : c
      );
      setTopbarClientsCache(dashboardCache.clients);
      writeSessionCache({
        clients: dashboardCache.clients,
        kpis: dashboardCache.kpis,
        lastUpdated: dashboardCache.lastUpdated,
        showClientAssignmentsColumn: dashboardCache.showClientAssignmentsColumn,
        companyName: dashboardCache.companyName,
        assignableProviders: dashboardCache.assignableProviders || [],
        onboardingStepTotal: dashboardCache.onboardingStepTotal ?? 16,
        userRole: dashboardCache.userRole ?? null,
      });
    } catch (err) {
      console.error('Error toggling client block status:', err);
      alert(err.message || 'Failed to update block status');
    } finally {
      setUpdatingBlockFor(prev => ({ ...prev, [client.user_code]: false }));
      setOpenKebabId(null);
    }
  };

  // Pure weight status for filter/sort (no i18n)
  const getWeightStatus = (client) => {
    const last = client.lastWeightUpdate;
    if (!last) return 'never';
    const lastDate = new Date(last);
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    return lastDate >= startOfWeek ? 'ok' : 'missing_week';
  };

  const clientPassesNonPulseFilters = useCallback((client) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (client.full_name && client.full_name.toLowerCase().includes(searchLower)) ||
      (client.email && client.email.toLowerCase().includes(searchLower)) ||
      (client.user_code && client.user_code.toLowerCase().includes(searchLower));
    const matchesMealPlan =
      filterMealPlan === 'all' ||
      (filterMealPlan === 'none' && (client.mealPlanStatus == null)) ||
      (client.mealPlanStatus === filterMealPlan);
    const matchesWorkoutPlan =
      filterWorkoutPlan === 'all' ||
      (filterWorkoutPlan === 'none' && (client.workoutPlanStatus == null)) ||
      (filterWorkoutPlan === 'active' && client.workoutPlanStatus === 'active') ||
      (filterWorkoutPlan === 'not_active' && client.workoutPlanStatus != null && client.workoutPlanStatus !== 'active');
    const weightStatus = getWeightStatus(client);
    const matchesWeight = filterWeight === 'all' || weightStatus === filterWeight;
    const matchesOnboarding =
      onboardingFilter === 'all' ||
      (onboardingFilter === 'incomplete' && !client.isOnboardingFinished);
    let matchesSubscription = true;
    if (filterSubscription !== 'all' && filterSubscription !== '') {
      const sub = client.subscription;
      const status = sub ? String(sub.status).toLowerCase() : null;
      const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
      if (filterSubscription === 'active') {
        matchesSubscription = status === 'active';
      } else if (filterSubscription === 'canceled') {
        matchesSubscription = (status === 'canceled' || status === 'cancelled') || (sub?.cancel_at_period_end === true);
      } else if (filterSubscription === 'ends_this_month') {
        matchesSubscription = !!periodEnd && periodEnd >= startOfMonth && periodEnd <= endOfMonth && status === 'active';
      } else {
        matchesSubscription = true;
      }
    }
    return matchesSearch && matchesMealPlan && matchesWorkoutPlan && matchesWeight && matchesSubscription && matchesOnboarding;
  }, [searchTerm, filterMealPlan, filterWorkoutPlan, filterWeight, onboardingFilter, filterSubscription]);

  const rosterAfterNonPulseFilters = useMemo(
    () => clients.filter(clientPassesNonPulseFilters),
    [clients, clientPassesNonPulseFilters]
  );

  // UI-only filters and sort — role filtering is done by the API
  const filteredClients = useMemo(() => {
    let list = rosterAfterNonPulseFilters.filter((client) =>
      filterPulse === 'attention' ? needsAttention(client) : matchesPulseFilter(client.engagementPulse, filterPulse)
    );
    // "Needs attention" reads most urgent first unless the coach chose a sort.
    if (filterPulse === 'attention' && !sortBy) {
      list = [...list].sort((a, b) => NEXT_STEP_RANK[clientNextStep(a).level] - NEXT_STEP_RANK[clientNextStep(b).level]);
    }

    if (sortBy) {
      const mult = sortOrder === 'asc' ? 1 : -1;
      const pulseRank = (p) => ({ healthy: 0, new: 1, onboarding: 2, '24h': 3, '72h': 4, never: 5 }[p] ?? -1);
      list = [...list].sort((a, b) => {
        let cmp = 0;
        switch (sortBy) {
          case 'name':
            cmp = (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '');
            break;
          case 'mealPlanStatus':
            cmp = (a.mealPlanStatus || '').localeCompare(b.mealPlanStatus || '');
            break;
          case 'statusUpdated': {
            const ta = a.mealPlanStatusUpdated ? new Date(a.mealPlanStatusUpdated).getTime() : 0;
            const tb = b.mealPlanStatusUpdated ? new Date(b.mealPlanStatusUpdated).getTime() : 0;
            cmp = ta - tb;
            break;
          }
          case 'weight': {
            const ta = a.lastWeightUpdate ? new Date(a.lastWeightUpdate).getTime() : 0;
            const tb = b.lastWeightUpdate ? new Date(b.lastWeightUpdate).getTime() : 0;
            cmp = ta - tb;
            break;
          }
          case 'workoutPlan':
            cmp = (a.workoutPlanStatus || '').localeCompare(b.workoutPlanStatus || '');
            break;
          case 'payment': {
            const va = a.subscription?.amount_total != null ? Number(a.subscription.amount_total) : 0;
            const vb = b.subscription?.amount_total != null ? Number(b.subscription.amount_total) : 0;
            cmp = va - vb;
            break;
          }
          case 'subscription': {
            const da = a.subscription?.current_period_end ? new Date(a.subscription.current_period_end).getTime() : 0;
            const db = b.subscription?.current_period_end ? new Date(b.subscription.current_period_end).getTime() : 0;
            cmp = da - db;
            break;
          }
          case 'engagementPulse':
            cmp = pulseRank(a.engagementPulse) - pulseRank(b.engagementPulse);
            break;
          default:
            break;
        }
        return mult * (cmp || 0);
      });
    }
    return list;
  }, [rosterAfterNonPulseFilters, filterPulse, sortBy, sortOrder]);

  useEffect(() => {
    setTableLimit(10);
  }, [searchTerm, filterPulse, filterMealPlan, filterSubscription, filterWorkoutPlan, filterWeight, onboardingFilter, sortBy, sortOrder]);


  // Pulse KPIs ignore the pulse filter so clicking Silent does not recount the card as 0
  const filteredKpis = useMemo(() => {
    const activeEngagement = rosterAfterNonPulseFilters.filter(c => c.engagementPulse === 'healthy').length;
    const quietZone = rosterAfterNonPulseFilters.filter(c => c.engagementPulse === '24h').length;
    const silentZone = rosterAfterNonPulseFilters.filter(c => c.engagementPulse === '72h' || c.engagementPulse === 'never').length;
    const onboardingIncomplete = rosterAfterNonPulseFilters.filter(c => c.engagementPulse === 'onboarding').length;
    return { activeEngagement, quietZone, silentZone, onboardingIncomplete, expectedIncomeThisMonth: kpis.expectedIncomeThisMonth ?? 0 };
  }, [rosterAfterNonPulseFilters, kpis.expectedIncomeThisMonth]);

  // --- TRANSLATIONS ---
  const translations = {
    english: {
      'admin.clientReferralLink': 'Client Referral Link',
      'admin.help.referralLink': 'Use this link to invite clients to work with our system: a permanent simple link, a limited link with max clients and expiry, or generate access codes for a free month of the AI bot.',
      'admin.referralLinkCopied': 'Link copied to clipboard',
      'admin.referral.title': 'Client Referral Link',
      'admin.referral.simple': 'Simple Link',
      'admin.referral.limited': 'Limited Link',
      'admin.referral.generateCodeTab': 'Generate code',
      'admin.referral.simpleDesc': 'Permanent link with unlimited clients',
      'admin.referral.targetCompany': 'Referral for company',
      'admin.referral.personalAccount': 'Your account (personal link)',
      'admin.referral.loadingCompanies': 'Loading companies…',
      'admin.referral.resolvingManager': 'Resolving company manager…',
      'admin.referral.limitedDesc': 'Link with max clients and optional expiry. You can choose per link whether clients skip the pricing step.',
      'admin.referral.generateCodeDesc': 'Create a 6-digit access code stored in the system. Each eligible client who redeems it gets one free month to use the AI bot. You choose how many clients can use the same code.',
      'admin.referral.maxClients': 'Max Clients',
      'admin.referral.expiryDate': 'Expiry Date',
      'admin.referral.expiryTime': 'Expiry Time (Optional)',
      'admin.referral.generate': 'Generate Limited Link',
      'admin.referral.copy': 'Copy Link',
      'admin.referral.editLanding': 'Edit landing page',
      'admin.referral.editLandingHint': 'This link opens your company landing page.',
      'admin.referral.landingPage': 'Landing page',
      'admin.referral.copyQR': 'Copy QR Code',
      'admin.referral.remove': 'Remove',
      'admin.referral.max': 'Max',
      'admin.referral.expires': 'Expires',
      'admin.referral.generatedLinks': 'Generated Links',
      'admin.referral.yourLinks': 'Your referral links',
      'admin.referral.slotsLeft': 'Slots left',
      'admin.referral.used': 'Used',
      'admin.referral.deactivate': 'Deactivate link',
      'admin.referral.active': 'Active',
      'admin.referral.inactive': 'Inactive',
      'admin.referral.loadingLinks': 'Loading links…',
      'admin.referral.noLinks': 'No referral links yet. Generate one below.',
      'admin.referral.prev': 'Previous',
      'admin.referral.next': 'Next',
      'admin.referral.created': 'Created',
      'admin.referral.owner': 'Owner',
      'admin.referral.accessCode': 'Access code',
      'admin.referral.onboardingCodeHelp': 'Share the code with your clients as you prefer. After they redeem it, they can use the AI bot at no charge for one month (until slots run out, if limited).',
      'admin.referral.generateCode': 'Generate 6-digit code',
      'admin.referral.clearCode': 'Clear',
      'admin.referral.copyCode': 'Copy code',
      'admin.referral.codeCopied': 'Code copied to clipboard',
      'admin.referral.codesHistoryTitle': 'Your codes',
      'admin.referral.showInactiveCodes': 'Show inactive, expired, or exhausted codes',
      'admin.referral.refreshCodes': 'Refresh',
      'admin.referral.loadingOnboardingCodes': 'Loading codes…',
      'admin.referral.noOnboardingCodes': 'No codes match this filter yet.',
      'admin.referral.usageColumn': 'Usage',
      'admin.referral.statusActive': 'Active',
      'admin.referral.statusInactive': 'Inactive',
      'admin.referral.statusExpired': 'Expired',
      'admin.referral.statusExhausted': 'Exhausted',
      'admin.referral.statusPending': 'Not yet valid',
      'admin.referral.deactivateCode': 'Deactivate code',
      'admin.referral.onboardingMaxSlots': 'How many clients can use this code',
      'admin.referral.onboardingMaxSlotsHelp': 'Each successful signup (or redemption) typically consumes one slot until the limit is reached.',
      'admin.referral.unlimitedSlots': 'Unlimited',
      'admin.referral.slotsForCode': 'Slots for this code',
      'admin.referral.slotsUnlimited': 'Unlimited',
      'admin.referral.skipPricingTitle': 'Skip pricing step',
      'admin.referral.skipPricingDesc': 'Clients who sign up via this link will skip the subscription / payment selection step.',
      'admin.referral.skipPricingBadge': 'No pricing',
      'admin.kpi.activeEngagement': 'Active Engagement',
      'admin.kpi.activeEngagementDesc': 'Clients active in last 24h',
      'admin.kpi.quietZone': 'Quiet Zone',
      'admin.kpi.quietZoneDesc': '24-72h silent (Retention risk)',
      'admin.kpi.silentZone': 'Silent Zone',
      'admin.kpi.silentZoneDesc': '72h+ silent (High churn risk)',
      'admin.kpi.expectedIncome': 'Expected income this month',
      'admin.kpi.expectedIncomeDesc': 'From active subscriptions',
      'admin.help.activeEngagement': 'Client has interacted with the bot in the last 24 hours.',
      'admin.help.quietZone': 'Client has been silent for 24–72 hours. A 24h template reminder is sent automatically via WhatsApp.',
      'admin.help.silentZone': 'High risk of churn (72h+ silent). A 72h template reminder is sent automatically via WhatsApp.',
      'admin.help.expectedIncome': 'From active subscriptions. Toggle ILS/USD here. Use "Receipts ↓" in the table to download Stripe invoice PDFs without logging into Stripe.',
      'admin.help.aiChatPreview': 'Preview recent messages, images, and audio here. Use "Show more" / "Show less" to expand. Triage quickly without opening the full chat.',
      'admin.help.weightTracking': 'Resets at the start of each week. Weight reminders are sent automatically to Healthy clients. If a client has not logged weight this week, they show as "Missing this week" in amber. Use the Filter menu to list them.',
      'admin.help.receipts': 'Opens payment history and lets you download Stripe invoice PDFs for this client without logging into the Stripe dashboard.',
      'admin.help.engagementPulse': 'Traffic-light retention tool: Healthy = active in last 24h (weight reminders sent automatically). Quiet = 24–72h silent (24h template reminder sent automatically via WhatsApp). Silent = 72h+ or never (72h template reminder sent automatically via WhatsApp).',
      'admin.help.workoutPlan': 'Filter to see clients with no active workout plan, or with a plan that is not active.',
      'admin.help.plans': 'Meal plan: status and when it was last updated. Workout plan: whether the client has an active training plan. Use the Filter menu to list by meal plan status or workout plan (active / not active / none).',
      'admin.help.onboardingProgressAria': 'Onboarding progress column help',
      'admin.help.onboardingProgress': `This column shows where each client is in signup. The bar fills as they complete steps. Color: muted (not started), warning (early steps), blue (high step, still open), forest only when marked completed.
Steps (in order):
1. Language 2. Name 3. Phone 4. Region 5. City 6. Timezone 7. Date of birth 8. Gender & nursing 9. Height & weight 10. Target weight 11. Medical conditions 12. Activity 13. Goal 14. Allergies 15. Limitations 16. Likes / dislikes 17. Eating window 18. Calories & macros 19. Meal planning 20. Subscription`,
      'admin.table.clientName': 'Client Name',
      'admin.table.onboardingProgress': 'Onboarding Progress',
      'admin.filter.onboardingAll': 'All - Onboarding',
      'admin.filter.onboardingIncomplete': 'Incomplete onboarding',
      'admin.onboarding.completed': 'Completed',
      'admin.onboarding.notStarted': 'Not Started',
      'admin.onboarding.step': 'Step',
      'admin.onboarding.subscriptionPayment': 'Subscription (pending payment)',
      'admin.onboarding.steps.name': 'Name',
      'admin.onboarding.steps.phone': 'Phone',
      'admin.onboarding.steps.location': 'Location',
      'admin.onboarding.steps.dob': 'Date of birth',
      'admin.onboarding.steps.gender': 'Gender',
      'admin.onboarding.steps.body': 'Height & weight',
      'admin.onboarding.steps.activity': 'Activity',
      'admin.onboarding.steps.goal': 'Goal',
      'admin.onboarding.steps.dietary': 'Allergies & limitations',
      'admin.onboarding.steps.preferences': 'Food preferences',
      'admin.onboarding.steps.eating_window': 'Eating window',
      'admin.onboarding.steps.custom': 'Custom questions',
      'admin.onboarding.steps.calories': 'Calories & macros',
      'admin.onboarding.steps.meals': 'Meals',
      'admin.onboarding.steps.medical': 'Medical conditions',
      'admin.table.weightMeasurement': 'Weight / Measurements',
      'admin.weight.updated': 'Updated',
      'admin.weight.missingThisWeek': 'Weight update missing this week',
      'admin.weight.never': 'No data yet',
      'admin.filter.subscription': 'Subscription',
      'admin.filter.subscriptionAll': 'All - Subscription',
      'admin.filter.subscriptionActive': 'Active subscription',
      'admin.filter.subscriptionCanceled': 'Canceled subscription',
      'admin.filter.subscriptionEndsThisMonth': 'Subscription ends this month',
      'admin.table.mealPlanStatus': 'Meal Plan Status',
      'admin.table.statusUpdated': 'Status Last Updated',
      'admin.table.workoutPlan': 'Workout Plan',
      'admin.table.aiChatPreview': 'AI Chat Preview',
      'admin.table.engagementPulse': 'Engagement Pulse',
      'admin.table.subscription': 'Subscription',
      'admin.table.payment': 'Payment',
      'admin.table.periodEnd': 'Period end',
      'admin.table.subscriptionType': 'Type',
      'admin.table.subscriptionStatus': 'Status',
      'admin.subscription.none': 'No subscription',
      'admin.subscription.cancelAtPeriodEnd': 'Cancels at period end',
      'admin.subscription.totalPaid': 'Total paid',
      'admin.subscription.receipts': 'Receipts',
      'admin.receipts.title': 'Payment history & receipts',
      'admin.receipts.totalPaid': 'Total paid',
      'admin.receipts.date': 'Date',
      'admin.receipts.amount': 'Amount',
      'admin.receipts.view': 'View',
      'admin.receipts.pdf': 'PDF',
      'admin.receipts.download': 'Receipt',
      'admin.receipts.close': 'Close',
      'admin.receipts.loading': 'Loading…',
      'admin.receipts.noReceipts': 'No receipts found',
      'admin.receipts.error': 'Failed to load receipts',
      'admin.table.clientAssignments': 'Client Assignments',
      'admin.help.clientAssignments': 'This column is for company managers and system admins. It shows which employee is assigned to each client (who can see them in the system).',
      'admin.help.clientAssignmentsWithCompany': 'This column is for company managers of {{companyName}} and system admins to manage which employees are assigned to which clients (who can see them in the system).',
      'admin.assign.onlyAdminCanChange': 'Only an admin can change',
      'admin.assign.assignedToAdmin': 'Assigned to admin',
      'admin.help.assignedToAdminReadonly': 'Assigned to admin. Only an admin can change this assignment.',
      'admin.help.digitalOnlySubscription': 'Usage-based pricing: each day the client uses the AI bot, an amount is deducted from what they owe for the subscription.',
      'admin.table.actions': 'Actions',
      'admin.table.load10More': 'Load 10 more',
      'admin.table.showAll': 'Show all',
      'admin.table.scrollMore': 'More columns — drag sideways or use the arrows',
      'admin.table.scrollPrev': 'Show earlier columns',
      'admin.table.scrollNext': 'Show more columns',
      'admin.filter.search': 'Search clients...',
      'admin.filter.button': 'Filter',
      'admin.filter.panelTitle': 'Filters',
      'admin.filter.back': 'Back',
      'admin.filter.clearAll': 'Clear all',
      'admin.filter.onboarding': 'Onboarding',
      'admin.filter.pulse': 'Engagement Pulse',
      'admin.filter.mealPlan': 'Meal Plan Status',
      'admin.filter.workoutPlan': 'Workout Plan',
      'admin.filter.weight': 'Weight / Measurements',
      'admin.filter.all': 'All',
      'admin.filter.workoutActive': 'Has active',
      'admin.filter.workoutNotActive': 'Has not active',
      'admin.filter.workoutNone': "Don't have",
      'admin.filter.weightOk': 'Updated',
      'admin.filter.weightMissingWeek': 'Missing this week',
      'admin.filter.weightNever': 'No data yet',
      'admin.sort.by': 'Sort by',
      'admin.sort.panelTitle': 'Sort',
      'admin.sort.clear': 'Clear sort',
      'admin.sort.direction': 'Order',
      'admin.sort.none': 'None',
      'admin.sort.name': 'Client Name',
      'admin.sort.mealPlanStatus': 'Meal Plan Status',
      'admin.sort.statusUpdated': 'Status Last Updated',
      'admin.sort.weight': 'Weight / Measurements',
      'admin.sort.workoutPlan': 'Workout Plan',
      'admin.sort.payment': 'Payment',
      'admin.sort.subscription': 'Subscription',
      'admin.sort.engagementPulse': 'Engagement Pulse',
      'admin.sort.asc': 'Ascending',
      'admin.sort.desc': 'Descending',
      'admin.status.draft': 'Draft',
      'admin.status.pending': 'Pending Activation',
      'admin.status.active': 'Active',
      'admin.status.cancelled': 'Cancelled',
      'admin.status.expired': 'Expired',
      'admin.status.notSet': 'Not Set',
      'admin.status.notActive': 'Not Active',
      'admin.status.not_active': 'Not Active',
      'admin.status.completed': 'Completed',
      'admin.status.paused': 'Paused',
      'admin.status.archived': 'Archived',
      'admin.pulse.healthy': 'Healthy',
      'admin.pulse.new': 'New Client',
      'admin.pulse.onboarding': 'Onboarding',
      'admin.pulse.24h': '24h Silent',
      'admin.pulse.silent': 'Silent (72h+ / never)',
      'admin.pulse.72h': '72h+ Silent',
      'admin.pulse.never': 'Never',
      'admin.action.view': 'View',
      'admin.action.chat': 'Chat',
      'admin.action.plan': 'Plan',
      'admin.action.blockClient': 'Block client',
      'admin.action.unblockClient': 'Unblock client',
      'admin.help.blockClientAction': 'Blocked clients cannot talk to the AI bot.',
      'admin.action.delete': 'Delete',
      'admin.delete.title': 'Delete Client',
      'admin.delete.warning': 'This action is permanent and irreversible.',
      'admin.delete.willDelete': 'Deleting this client will remove:',
      'admin.delete.mealPlans': 'All meal plans',
      'admin.delete.messages': 'All chat messages',
      'admin.delete.conversations': 'All conversations',
      'admin.delete.clientProfile': 'The client profile',
      'admin.delete.confirm': 'Type DELETE to confirm',
      'admin.delete.cancel': 'Cancel',
      'admin.delete.submit': 'Delete Client Permanently',
      'admin.chat.showMore': 'Show more',
      'admin.chat.showLess': 'Show less',
      'admin.chat.noMessages': 'No messages yet',
      'admin.time.ago': 'ago',
      'admin.time.hours': 'hours',
      'admin.time.days': 'days',
      'admin.time.weeks': 'weeks',
      'admin.loading': 'Loading dashboard data...',
      'admin.loadingHint': 'Fetching your clients and statistics. This usually takes a few seconds.',
      'admin.refreshing': 'Refreshing dashboard…',
      'admin.error': 'Error loading dashboard',
      'admin.noClients': 'No clients found',
      'admin.refresh': 'Refresh',
      'admin.lastUpdated': 'Last updated',
      'admin.list.title': 'Clients',
      'admin.list.desc': 'Click a client to see where they stand, then open them to work on their plan, chat and logs.',
      'admin.income.label': 'Expected this month',
      'admin.invite': 'Invite clients',
      'admin.tab.all': 'All',
      'admin.tab.attention': 'Needs attention',
      'admin.tab.active': 'Active',
      'admin.tab.quiet': 'Quiet',
      'admin.tab.silent': 'Silent',
      'admin.tab.onboarding': 'Onboarding',
      'admin.col.client': 'Client',
      'admin.col.lastActive': 'Last active',
      'admin.col.plans': 'Plans',
      'admin.col.payment': 'Payment',
      'admin.col.next': 'Next step',
      'admin.next.payment': 'Payment past due',
      'admin.next.silent': 'Silent · last seen {ago}',
      'admin.next.never': 'Never opened the app',
      'admin.next.stuck': 'Stuck at onboarding step {n}',
      'admin.next.onboarding': 'Onboarding · step {n} of {total}',
      'admin.next.noMealPlan': 'No active meal plan',
      'admin.next.quiet': 'Quiet for a day · check in',
      'admin.next.cancelling': 'Cancels at period end',
      'admin.next.noWeighIn': 'No weigh-in this week',
      'admin.next.ok': 'All good',
      'admin.peek.open': 'Open client',
      'admin.peek.close': 'Close',
      'admin.peek.lastMessages': 'Recent messages',
      'admin.peek.hint': 'Click a client for a quick look · double-click to open them',
    },
    hebrew: {
      'admin.clientReferralLink': 'קישור הפניה ללקוח',
      'admin.help.referralLink': 'השתמש בקישור זה להזמנת לקוחות: קישור קבוע, קישור מוגבל עם מקסימום לקוחות ותפוגה, או יצירת קודי גישה לחודש חינם של בוט ה-AI.',
      'admin.referralLinkCopied': 'הקישור הועתק ללוח',
      'admin.referral.title': 'קישור הפניה ללקוח',
      'admin.referral.simple': 'קישור פשוט',
      'admin.referral.limited': 'קישור מוגבל',
      'admin.referral.generateCodeTab': 'צור קוד',
      'admin.referral.simpleDesc': 'קישור קבוע ללא הגבלת לקוחות',
      'admin.referral.targetCompany': 'קישור עבור חברה',
      'admin.referral.personalAccount': 'החשבון שלך (אישי)',
      'admin.referral.loadingCompanies': 'טוען חברות…',
      'admin.referral.resolvingManager': 'מאתר מנהל חברה…',
      'admin.referral.limitedDesc': 'קישור עם מקסימום לקוחות ותפוגה אופציונלית. ניתן לבחור לכל קישור בנפרד אם לדלג על שלב התמחור.',
      'admin.referral.generateCodeDesc': 'צור קוד גישה בן 6 ספרות שנשמר במערכת. כל לקוח זכאי שמממש את הקוד מקבל חודש אחד חינם לשימוש בבוט ה-AI. אתה בוחר כמה לקוחות יכולים להשתמש באותו קוד.',
      'admin.referral.maxClients': 'מקסימום לקוחות',
      'admin.referral.expiryDate': 'תאריך תפוגה',
      'admin.referral.expiryTime': 'שעת תפוגה (אופציונלי)',
      'admin.referral.generate': 'צור קישור מוגבל',
      'admin.referral.copy': 'העתק קישור',
      'admin.referral.editLanding': 'ערוך דף נחיתה',
      'admin.referral.editLandingHint': 'הקישור הזה פותח את דף הנחיתה של החברה.',
      'admin.referral.landingPage': 'דף נחיתה',
      'admin.referral.copyQR': 'העתק QR code',
      'admin.referral.remove': 'הסר',
      'admin.referral.max': 'מקס',
      'admin.referral.expires': 'תפוגה',
      'admin.referral.generatedLinks': 'קישורים שנוצרו',
      'admin.referral.yourLinks': 'קישורי ההפניה שלך',
      'admin.referral.slotsLeft': 'מקומות פנויים',
      'admin.referral.used': 'בשימוש',
      'admin.referral.deactivate': 'בטל קישור',
      'admin.referral.active': 'פעיל',
      'admin.referral.inactive': 'לא פעיל',
      'admin.referral.loadingLinks': 'טוען קישורים…',
      'admin.referral.noLinks': 'אין עדיין קישורי הפניה. צור אחד למטה.',
      'admin.referral.prev': 'הקודם',
      'admin.referral.next': 'הבא',
      'admin.referral.created': 'נוצר',
      'admin.referral.owner': 'בעלים',
      'admin.referral.accessCode': 'קוד גישה',
      'admin.referral.onboardingCodeHelp': 'שתף את הקוד עם הלקוחות בדרך שנוחה לך. אחרי מימוש, הם יכולים להשתמש בבוט ה-AI חינם במשך חודש (עד שנגמרו המקומות, אם הגבלת).',
      'admin.referral.generateCode': 'צור קוד בן 6 ספרות',
      'admin.referral.clearCode': 'נקה',
      'admin.referral.copyCode': 'העתק קוד',
      'admin.referral.codeCopied': 'הקוד הועתק ללוח',
      'admin.referral.codesHistoryTitle': 'הקודים שלך',
      'admin.referral.showInactiveCodes': 'הצג קודים לא פעילים, שפג תוקפם או מלאים',
      'admin.referral.refreshCodes': 'רענן',
      'admin.referral.loadingOnboardingCodes': 'טוען קודים…',
      'admin.referral.noOnboardingCodes': 'אין קודים שמתאימים למסנן.',
      'admin.referral.usageColumn': 'שימוש',
      'admin.referral.statusActive': 'פעיל',
      'admin.referral.statusInactive': 'לא פעיל',
      'admin.referral.statusExpired': 'פג תוקף',
      'admin.referral.statusExhausted': 'מלא',
      'admin.referral.statusPending': 'טרם בתוקף',
      'admin.referral.deactivateCode': 'בטל קוד',
      'admin.referral.onboardingMaxSlots': 'כמה לקוחות יכולים להשתמש בקוד',
      'admin.referral.onboardingMaxSlotsHelp': 'כל הרשמה מוצלחת (או מימוש) בדרך כלל צורכת מקום אחד עד למגבלה.',
      'admin.referral.unlimitedSlots': 'ללא הגבלה',
      'admin.referral.slotsForCode': 'מקומות לקוד זה',
      'admin.referral.slotsUnlimited': 'ללא הגבלה',
      'admin.referral.skipPricingTitle': 'דלג על שלב התמחור',
      'admin.referral.skipPricingDesc': 'לקוחות שנרשמים דרך קישור זה ידלגו על שלב בחירת מנוי / תשלום.',
      'admin.referral.skipPricingBadge': 'ללא תמחור',
      'admin.kpi.activeEngagement': 'מעורבות פעילה',
      'admin.kpi.activeEngagementDesc': 'לקוחות פעילים ב-24 שעות האחרונות',
      'admin.kpi.quietZone': 'אזור שקט',
      'admin.kpi.quietZoneDesc': '24-72 שעות שקט (סיכון שימור)',
      'admin.kpi.silentZone': 'אזור שקט',
      'admin.kpi.silentZoneDesc': '72+ שעות שקט (סיכון נשירה גבוה)',
      'admin.kpi.expectedIncome': 'הכנסה צפויה החודש',
      'admin.kpi.expectedIncomeDesc': 'ממנויים פעילים',
      'admin.help.activeEngagement': 'הלקוח השתמש בבוט ב-24 השעות האחרונות.',
      'admin.help.quietZone': 'הלקוח שקט 24–72 שעות. תזכורת תבנית 24 שעות נשלחת אוטומטית בוואטסאפ.',
      'admin.help.silentZone': 'סיכון נשירה גבוה (72+ שעות שקט). תזכורת תבנית 72 שעות נשלחת אוטומטית בוואטסאפ.',
      'admin.help.expectedIncome': 'ממנויים פעילים. החלף ₪/$. השתמש ב"קבלות ↓" בטבלה להורדת PDF מסטרייף בלי להיכנס לסטרייף.',
      'admin.help.aiChatPreview': 'תצוגה מקדימה של הודעות, תמונות ואודיו. "הצג עוד/פחות" להרחבה. סינון מהיר בלי לפתוח את הצ\'אט.',
      'admin.help.weightTracking': 'מתאפס בתחילת כל שבוע. תזכורות משקל נשלחות אוטומטית ללקוחות "בריאים". אם לא הוזן משקל השבוע – יופיע "חסר השבוע" בכתום. השתמש בתפריט "סינון".',
      'admin.help.receipts': 'פותח היסטוריית תשלומים ומאפשר הורדת PDF קבלות מסטרייף בלי להיכנס לסטרייף.',
      'admin.help.engagementPulse': 'דופק שימור: בריא = פעיל ב-24 שעות (תזכורות משקל אוטומטיות); שקט = 24–72 שעות (תבנית 24 שעות נשלחת אוטומטית בוואטסאפ); שקט ארוך = 72+ או מעולם (תבנית 72 שעות נשלחת אוטומטית בוואטסאפ).',
      'admin.help.workoutPlan': 'סנן ללקוחות ללא תוכנית אימונים פעילה או עם תוכנית לא פעילה.',
      'admin.help.plans': 'תוכנית ארוחות: סטטוס ומתי עודכן לאחרונה. תוכנית אימונים: האם ללקוח יש תוכנית אימונים פעילה. השתמש בתפריט "סינון" למיון לפי סטטוס תוכנית ארוחות או תוכנית אימונים (פעיל / לא פעיל / אין).',
      'admin.help.onboardingProgressAria': 'עזרה: עמודת התקדמות הצטרפות',
      'admin.help.onboardingProgress': `העמודה מציגה היכן כל לקוח בתהליך ההרשמה. הפס מתמלא לפי השלבים שהושלמו. צבע: אפור (לא התחיל), אזהרה (תחילת הדרך), כחול (שלב גבוה — עדיין פתוח), ירוק יער רק כשסומן כהושלם.
השלבים (לפי סדר):
1. שפה 2. שם 3. טלפון 4. אזור 5. עיר 6. אזור זמן 7. תאריך לידה 8. מגדר והנקה 9. גובה ומשקל 10. משקל יעד 11. מצבים רפואיים 12. רמת פעילות 13. מטרה 14. אלרגיות 15. מגבלות תזונה 16. העדפות / מה לא אוהבים 17. חלון אכילה 18. קלוריות ומאקרו 19. תכנון ארוחות 20. מנוים.`,
      'admin.table.clientName': 'שם לקוח',
      'admin.table.onboardingProgress': 'התקדמות הצטרפות',
      'admin.filter.onboardingAll': 'הכל - הצטרפות',
      'admin.filter.onboardingIncomplete': 'הצטרפות לא הושלמה',
      'admin.onboarding.completed': 'הושלם',
      'admin.onboarding.notStarted': 'לא התחיל',
      'admin.onboarding.step': 'שלב',
      'admin.onboarding.subscriptionPayment': 'מנוי (ממתין לתשלום)',
      'admin.onboarding.steps.name': 'שם',
      'admin.onboarding.steps.phone': 'טלפון',
      'admin.onboarding.steps.location': 'מיקום',
      'admin.onboarding.steps.dob': 'תאריך לידה',
      'admin.onboarding.steps.gender': 'מין',
      'admin.onboarding.steps.body': 'גובה ומשקל',
      'admin.onboarding.steps.activity': 'פעילות',
      'admin.onboarding.steps.goal': 'מטרה',
      'admin.onboarding.steps.dietary': 'אלרגיות ומגבלות',
      'admin.onboarding.steps.preferences': 'העדפות מזון',
      'admin.onboarding.steps.eating_window': 'חלון אכילה',
      'admin.onboarding.steps.custom': 'שאלות מותאמות',
      'admin.onboarding.steps.calories': 'קלוריות ומאקרו',
      'admin.onboarding.steps.meals': 'ארוחות',
      'admin.onboarding.steps.medical': 'מצב רפואי',
      'admin.table.weightMeasurement': 'משקל / מדידות',
      'admin.weight.updated': 'עודכן',
      'admin.weight.missingThisWeek': 'עדכון משקל חסר השבוע',
      'admin.weight.never': 'אין נתונים עדיין',
      'admin.filter.subscription': 'מנוי',
      'admin.filter.subscriptionAll': 'הכל - מנוי',
      'admin.filter.subscriptionActive': 'מנוי פעיל',
      'admin.filter.subscriptionCanceled': 'מנוי מבוטל',
      'admin.filter.subscriptionEndsThisMonth': 'מנוי מסתיים החודש',
      'admin.table.mealPlanStatus': 'סטטוס תוכנית ארוחות',
      'admin.table.statusUpdated': 'עדכון אחרון',
      'admin.table.workoutPlan': 'תוכנית אימונים',
      'admin.table.aiChatPreview': 'תצוגה מקדימה של צ\'אט AI',
      'admin.table.engagementPulse': 'דופק מעורבות',
      'admin.table.subscription': 'מנוי',
      'admin.table.payment': 'תשלום',
      'admin.table.periodEnd': 'סיום תקופה',
      'admin.table.subscriptionType': 'סוג',
      'admin.table.subscriptionStatus': 'סטטוס',
      'admin.subscription.none': 'אין מנוי',
      'admin.subscription.cancelAtPeriodEnd': 'יבוטל בסוף התקופה',
      'admin.subscription.totalPaid': 'סה"כ שולם',
      'admin.subscription.receipts': 'קבלות',
      'admin.receipts.title': 'היסטוריית תשלומים וקבלות',
      'admin.receipts.totalPaid': 'סה"כ שולם',
      'admin.receipts.date': 'תאריך',
      'admin.receipts.amount': 'סכום',
      'admin.receipts.view': 'צפה',
      'admin.receipts.pdf': 'PDF',
      'admin.receipts.download': 'קבלה',
      'admin.receipts.close': 'סגור',
      'admin.receipts.loading': 'טוען…',
      'admin.receipts.noReceipts': 'לא נמצאו קבלות',
      'admin.receipts.error': 'טעינת קבלות נכשלה',
      'admin.table.clientAssignments': 'הקצאת לקוחות',
      'admin.help.clientAssignments': 'עמודה זו מיועדת למנהלי חברה ומנהלי מערכת. היא מציגה איזה עובד משויך לכל לקוח (מי יכול לראות אותם במערכת).',
      'admin.help.clientAssignmentsWithCompany': 'עמודה זו מיועדת למנהלי החברה של {{companyName}} ומנהלי מערכת לניהול אילו עובדים משויכים לאילו לקוחות (מי יכול לראות אותם במערכת).',
      'admin.assign.onlyAdminCanChange': 'רק מנהל מערכת יכול לשנות',
      'admin.assign.assignedToAdmin': 'משויך למנהל מערכת',
      'admin.help.assignedToAdminReadonly': 'משויך למנהל מערכת. רק מנהל מערכת יכול לשנות הקצאה זו.',
      'admin.help.digitalOnlySubscription': 'תמחור לפי שימוש: בכל יום שהלקוח משתמש בבוט ה-AI, מופחת סכום מיתרת המנוי.',
      'admin.table.actions': 'פעולות',
      'admin.table.load10More': 'טען 10 נוספים',
      'admin.table.showAll': 'הצג הכל',
      'admin.table.scrollMore': 'עוד עמודות — גרור הצידה או השתמש בחצים',
      'admin.table.scrollPrev': 'הצג עמודות קודמות',
      'admin.table.scrollNext': 'הצג עוד עמודות',
      'admin.filter.search': 'חפש לקוחות...',
      'admin.filter.button': 'סינון',
      'admin.filter.panelTitle': 'מסננים',
      'admin.filter.back': 'חזרה',
      'admin.filter.clearAll': 'נקה הכל',
      'admin.filter.onboarding': 'הצטרפות',
      'admin.filter.pulse': 'דופק מעורבות',
      'admin.filter.mealPlan': 'סטטוס תוכנית ארוחות',
      'admin.filter.workoutPlan': 'תוכנית אימונים',
      'admin.filter.weight': 'משקל / מדידות',
      'admin.filter.all': 'הכל',
      'admin.filter.workoutActive': 'יש פעיל',
      'admin.filter.workoutNotActive': 'יש לא פעיל',
      'admin.filter.workoutNone': 'אין',
      'admin.filter.weightOk': 'עודכן',
      'admin.filter.weightMissingWeek': 'חסר השבוע',
      'admin.filter.weightNever': 'אין נתונים',
      'admin.sort.by': 'מיון לפי',
      'admin.sort.panelTitle': 'מיון',
      'admin.sort.clear': 'איפוס מיון',
      'admin.sort.direction': 'סדר',
      'admin.sort.none': 'ללא',
      'admin.sort.name': 'שם לקוח',
      'admin.sort.mealPlanStatus': 'סטטוס תוכנית ארוחות',
      'admin.sort.statusUpdated': 'עדכון אחרון',
      'admin.sort.weight': 'משקל / מדידות',
      'admin.sort.workoutPlan': 'תוכנית אימונים',
      'admin.sort.payment': 'תשלום',
      'admin.sort.subscription': 'מנוי',
      'admin.sort.engagementPulse': 'דופק מעורבות',
      'admin.sort.asc': 'עולה',
      'admin.sort.desc': 'יורד',
      'admin.status.draft': 'טיוטה',
      'admin.status.pending': 'ממתין להפעלה',
      'admin.status.active': 'פעיל',
      'admin.status.cancelled': 'בוטל',
      'admin.status.expired': 'פג תוקף',
      'admin.status.notSet': 'לא הוגדר',
      'admin.status.notActive': 'לא פעיל',
      'admin.status.not_active': 'לא פעיל',
      'admin.status.completed': 'הושלם',
      'admin.status.paused': 'מושהה',
      'admin.status.archived': 'בארכיון',
      'admin.pulse.healthy': 'בריא',
      'admin.pulse.new': 'לקוח חדש',
      'admin.pulse.onboarding': 'קליטה',
      'admin.pulse.24h': '24 שעות שקט',
      'admin.pulse.silent': 'דומם (72+ / מעולם לא)',
      'admin.pulse.72h': '72+ שעות שקט',
      'admin.pulse.never': 'מעולם לא',
      'admin.action.view': 'צפה',
      'admin.action.chat': 'צ\'אט',
      'admin.action.plan': 'תוכנית',
      'admin.action.blockClient': 'חסום לקוח',
      'admin.action.unblockClient': 'בטל חסימת לקוח',
      'admin.help.blockClientAction': 'לקוח חסום לא יוכל לדבר עם בוט ה-AI.',
      'admin.action.delete': 'מחק',
      'admin.delete.title': 'מחק לקוח',
      'admin.delete.warning': 'פעולה זו קבועה ובלתי הפיכה.',
      'admin.delete.willDelete': 'מחיקת לקוח זה תסיר:',
      'admin.delete.mealPlans': 'את כל תוכניות הארוחות',
      'admin.delete.messages': 'את כל הודעות הצ\'אט',
      'admin.delete.conversations': 'את כל השיחות',
      'admin.delete.clientProfile': 'את פרופיל הלקוח',
      'admin.delete.confirm': 'הקלד מחק כדי לאשר',
      'admin.delete.cancel': 'ביטול',
      'admin.delete.submit': 'מחק לקוח לצמיתות',
      'admin.chat.showMore': 'הצג עוד',
      'admin.chat.showLess': 'הצג פחות',
      'admin.chat.noMessages': 'אין הודעות עדיין',
      'admin.time.ago': 'לפני',
      'admin.time.hours': 'שעות',
      'admin.time.days': 'ימים',
      'admin.time.weeks': 'שבועות',
      'admin.loading': 'טוען נתוני לוח בקרה...',
      'admin.loadingHint': 'טוען לקוחות וסטטיסטיקות. בדרך כלל לוקח כמה שניות.',
      'admin.refreshing': 'מרענן את לוח הבקרה…',
      'admin.error': 'שגיאה בטעינת לוח הבקרה',
      'admin.noClients': 'לא נמצאו לקוחות',
      'admin.refresh': 'רענון',
      'admin.lastUpdated': 'עודכן לאחרונה',
      'admin.list.title': 'לקוחות',
      'admin.list.desc': 'לחצו על לקוח כדי לראות איפה הוא עומד, ואז פתחו אותו כדי לעבוד על התוכנית, הצ׳אט והיומנים.',
      'admin.income.label': 'צפי החודש',
      'admin.invite': 'הזמנת לקוחות',
      'admin.tab.all': 'הכל',
      'admin.tab.attention': 'דורשים התייחסות',
      'admin.tab.active': 'פעילים',
      'admin.tab.quiet': 'שקטים',
      'admin.tab.silent': 'דוממים',
      'admin.tab.onboarding': 'בקליטה',
      'admin.col.client': 'לקוח',
      'admin.col.lastActive': 'פעילות אחרונה',
      'admin.col.plans': 'תוכניות',
      'admin.col.payment': 'תשלום',
      'admin.col.next': 'הצעד הבא',
      'admin.next.payment': 'תשלום באיחור',
      'admin.next.silent': 'דומם · נראה לאחרונה {ago}',
      'admin.next.never': 'לא פתח את האפליקציה אף פעם',
      'admin.next.stuck': 'תקוע בשלב {n} בקליטה',
      'admin.next.onboarding': 'בקליטה · שלב {n} מתוך {total}',
      'admin.next.noMealPlan': 'אין תוכנית תזונה פעילה',
      'admin.next.quiet': 'שקט כבר יום · כדאי לפנות',
      'admin.next.cancelling': 'מבטל בסוף התקופה',
      'admin.next.noWeighIn': 'אין שקילה השבוע',
      'admin.next.ok': 'הכל תקין',
      'admin.peek.open': 'פתיחת הלקוח',
      'admin.peek.close': 'סגירה',
      'admin.peek.lastMessages': 'הודעות אחרונות',
      'admin.peek.hint': 'לחיצה על לקוח למבט מהיר · לחיצה כפולה לפתיחה',
    }
  };
  const t = (key) => translations[language]?.[key] || key;

  const referralCompanySelectLabel = !referralTargetCompanyId
    ? t('admin.referral.personalAccount')
    : referralCompanies.find((x) => String(x.id) === String(referralTargetCompanyId))?.name ||
      t('admin.referral.personalAccount');

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterPulse !== 'all') n += 1;
    if (filterMealPlan !== 'all') n += 1;
    if (filterSubscription !== 'all') n += 1;
    if (filterWorkoutPlan !== 'all') n += 1;
    if (filterWeight !== 'all') n += 1;
    if (onboardingFilter !== 'all') n += 1;
    return n;
  }, [filterPulse, filterMealPlan, filterSubscription, filterWorkoutPlan, filterWeight, onboardingFilter]);

  const clearAllAdminFilters = useCallback(() => {
    setFilterPulse('all');
    setFilterMealPlan('all');
    setFilterSubscription('all');
    setFilterWorkoutPlan('all');
    setFilterWeight('all');
    setOnboardingFilter('all');
  }, []);

  const adminFilterCategories = useMemo(
    () => [
      {
        id: 'pulse',
        title: t('admin.filter.pulse'),
        value: filterPulse,
        setValue: setFilterPulse,
        options: [
          { value: 'all', label: t('admin.filter.all') },
          { value: 'healthy', label: t('admin.pulse.healthy') },
          { value: 'new', label: t('admin.pulse.new') },
          { value: 'onboarding', label: t('admin.pulse.onboarding') },
          { value: '24h', label: t('admin.pulse.24h') },
          { value: 'silent', label: t('admin.pulse.silent') },
          { value: '72h', label: t('admin.pulse.72h') },
          { value: 'never', label: t('admin.pulse.never') },
        ],
      },
      {
        id: 'mealPlan',
        title: t('admin.filter.mealPlan'),
        value: filterMealPlan,
        setValue: setFilterMealPlan,
        options: [
          { value: 'all', label: t('admin.filter.all') },
          { value: 'active', label: t('admin.status.active') },
          { value: 'not_active', label: t('admin.status.notActive') },
          { value: 'none', label: t('admin.status.notSet') },
        ],
      },
      {
        id: 'subscription',
        title: t('admin.filter.subscription'),
        value: filterSubscription,
        setValue: setFilterSubscription,
        options: [
          { value: 'all', label: t('admin.filter.subscriptionAll') },
          { value: 'active', label: t('admin.filter.subscriptionActive') },
          { value: 'canceled', label: t('admin.filter.subscriptionCanceled') },
          { value: 'ends_this_month', label: t('admin.filter.subscriptionEndsThisMonth') },
        ],
      },
      {
        id: 'workoutPlan',
        title: t('admin.filter.workoutPlan'),
        value: filterWorkoutPlan,
        setValue: setFilterWorkoutPlan,
        options: [
          { value: 'all', label: t('admin.filter.all') },
          { value: 'active', label: t('admin.filter.workoutActive') },
          { value: 'not_active', label: t('admin.filter.workoutNotActive') },
          { value: 'none', label: t('admin.filter.workoutNone') },
        ],
      },
      {
        id: 'weight',
        title: t('admin.filter.weight'),
        value: filterWeight,
        setValue: setFilterWeight,
        options: [
          { value: 'all', label: t('admin.filter.all') },
          { value: 'ok', label: t('admin.filter.weightOk') },
          { value: 'missing_week', label: t('admin.filter.weightMissingWeek') },
          { value: 'never', label: t('admin.filter.weightNever') },
        ],
      },
      {
        id: 'onboarding',
        title: t('admin.filter.onboarding'),
        value: onboardingFilter,
        setValue: setOnboardingFilter,
        options: [
          { value: 'all', label: t('admin.filter.onboardingAll') },
          { value: 'incomplete', label: t('admin.filter.onboardingIncomplete') },
        ],
      },
    ],
    [
      language,
      filterPulse,
      filterMealPlan,
      filterSubscription,
      filterWorkoutPlan,
      filterWeight,
      onboardingFilter,
    ]
  );

  const clearSort = useCallback(() => {
    setSortBy('');
    setSortOrder('asc');
  }, []);

  const adminSortColumns = useMemo(
    () => [
      { id: 'name', title: t('admin.sort.name') },
      { id: 'mealPlanStatus', title: t('admin.sort.mealPlanStatus') },
      { id: 'weight', title: t('admin.sort.weight') },
      { id: 'subscription', title: t('admin.sort.subscription') },
      { id: 'engagementPulse', title: t('admin.sort.engagementPulse') },
    ],
    [language]
  );

  useEffect(() => {
    if (!filterPanelOpen && !sortPanelOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (filterPanelOpen) {
        if (filterPanelCategory) setFilterPanelCategory(null);
        else {
          setFilterPanelOpen(false);
          setFilterPanelCategory(null);
        }
        return;
      }
      if (sortPanelOpen) {
        if (sortPanelColumn) setSortPanelColumn(null);
        else {
          setSortPanelOpen(false);
          setSortPanelColumn(null);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [filterPanelOpen, filterPanelCategory, sortPanelOpen, sortPanelColumn]);

  useEffect(() => {
    if (!filterPanelOpen && !sortPanelOpen) return;
    const onDown = (e) => {
      if (filterPanelOpen && filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setFilterPanelOpen(false);
        setFilterPanelCategory(null);
      }
      if (sortPanelOpen && sortPanelRef.current && !sortPanelRef.current.contains(e.target)) {
        setSortPanelOpen(false);
        setSortPanelColumn(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [filterPanelOpen, sortPanelOpen]);

  // --- HELPER FUNCTIONS ---
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return t('admin.pulse.never');
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} ${t('admin.time.hours')} ${t('admin.time.ago')}`;
    if (diffDays < 7) return `${diffDays} ${t('admin.time.days')} ${t('admin.time.ago')}`;
    return `${diffWeeks} ${t('admin.time.weeks')} ${t('admin.time.ago')}`;
  };

  const formatInteractionType = (type) => {
    if (!type || type === 'account_created') return null;
    switch(type) {
      case 'chat': return language === 'hebrew' ? '(צ\'אט)' : '(Chat)';
      case 'food': return language === 'hebrew' ? '(יומן אוכל)' : '(Food log)';
      case 'weight': return language === 'hebrew' ? '(שקילה)' : '(Weight)';
      default: return '';
    }
  };

  const resolvePlanStatus = (status) => {
    if (status == null || status === '') return 'not_active';
    return status;
  };

  const getStatusColor = (status) => {
    switch (resolvePlanStatus(status)) {
      case 'active':
        return 'bg-forest-faint text-forest dark:bg-forest-faint-dark dark:text-forest-ondark';
      case 'completed':
        return 'bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark border border-line dark:border-line-dark';
      case 'paused':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400';
      case 'archived':
        return 'bg-canvas dark:bg-canvas-dark text-ink-muted dark:text-ink-muted-dark';
      case 'not_active':
        return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400';
      default:
        return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (resolvePlanStatus(status)) {
      case 'active':
      case 'completed':
        return Check;
      case 'paused':
        return Pause;
      case 'archived':
        return CircleOff;
      case 'not_active':
      default:
        return Ban;
    }
  };

  const StatusPill = ({ status, label }) => {
    const resolved = resolvePlanStatus(status);
    const Icon = getStatusIcon(resolved);
    const text = label || t(`admin.status.${resolved}`);
    return (
      <span className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${getStatusColor(resolved)}`}>
        <Icon size={11} strokeWidth={2.5} aria-hidden />
        {text}
      </span>
    );
  };

  const getPulseColor = (pulse) => {
    switch (pulse) {
      case 'healthy':
        return 'text-forest dark:text-forest-ondark font-medium';
      case 'new':
      case 'onboarding':
        return 'text-[color:var(--progress)] font-medium';
      case '24h':
        return 'text-amber-800 dark:text-amber-400 font-medium';
      case '72h':
      case 'never':
        return 'text-red-700 dark:text-red-400 font-medium';
      default:
        return 'text-ink-muted dark:text-ink-muted-dark';
    }
  };

  const getPulseDotColor = (pulse) => {
    switch (pulse) {
      case 'healthy': return 'bg-forest';
      case 'new':
      case 'onboarding': return 'bg-[color:var(--progress)]';
      case '24h':     return 'bg-amber-700';
      case '72h':
      case 'never':   return 'bg-red-700';
      default:        return 'bg-ink-muted';
    }
  };

  const getSubscriptionStatusColor = (status) => {
    if (!status) return 'bg-canvas dark:bg-canvas-dark text-ink-muted dark:text-ink-muted-dark';
    switch (String(status).toLowerCase()) {
      case 'active':
        return 'bg-forest-faint text-forest dark:bg-forest-faint-dark dark:text-forest-ondark';
      case 'canceled':
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400';
      case 'past_due':
      case 'unpaid':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400';
      case 'trialing':
        return 'bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark border border-line dark:border-line-dark';
      default:
        return 'bg-canvas dark:bg-canvas-dark text-ink-muted dark:text-ink-muted-dark';
    }
  };

  const formatSubscriptionAmount = (subscription, usdToIlsRateForDisplay) => {
    if (!subscription || subscription.amount_total == null) return null;
    const amount = Number(subscription.amount_total);
    const currency = (subscription.currency || 'USD').toUpperCase();
    const rate = usdToIlsRateForDisplay ?? kpis.usdToIlsRate ?? 3.7;
    const isUsd = currency === 'USD' || currency === '$';
    const isIls = currency === 'ILS' || currency === 'NIS';
    if (isIls) return `₪${amount.toFixed(2)} ILS`;
    if (isUsd && rate != null) {
      const ils = amount * rate;
      return `$${amount.toFixed(2)} USD (≈ ₪${ils.toFixed(0)})`;
    }
    if (currency === 'EUR') return `€${amount.toFixed(2)} EUR`;
    return `${currency} ${amount.toFixed(2)}`;
  };

  const formatSubscriptionDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString(language === 'hebrew' ? 'he-IL' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getWeightMeasurementStatus = (client) => {
    const last = client.lastWeightUpdate;
    if (!last) return { label: t('admin.weight.never'), status: 'never' };
    const lastDate = new Date(last);
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    if (lastDate >= startOfWeek) {
      const days = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
      const timeAgo = days === 0 ? (language === 'hebrew' ? 'היום' : 'Today') : days === 1 ? (language === 'hebrew' ? 'אתמול' : 'Yesterday') : `${days} ${t('admin.time.days')} ${t('admin.time.ago')}`;
      return { label: `${t('admin.weight.updated')} ${timeAgo}`, status: 'ok' };
    }
    return { label: t('admin.weight.missingThisWeek'), status: 'missing_week' };
  };

  const formatExpectedIncome = (amount, inCurrency = 'ILS') => {
    if (amount == null || Number.isNaN(amount)) return inCurrency === 'USD' ? '$0' : '₪0';
    const rate = kpis.usdToIlsRate ?? 3.7;
    if (inCurrency === 'USD') {
      const usd = Number(amount) / rate;
      if (usd >= 1000) return `$${(usd / 1000).toFixed(1)}k`;
      return `$${usd.toFixed(0)}`;
    }
    const n = Number(amount);
    if (n >= 1000) return `₪${(n / 1000).toFixed(1)}k`;
    return `₪${n.toFixed(0)}`;
  };

  const parseMessageContent = (message) => {
    if (!message) return '';
    const content = message.content || message.message || '';
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      if (parsed && typeof parsed === 'object' && parsed.response_text) {
        return parsed.response_text;
      }
    } catch (e) {
      // Not JSON
    }
    return content;
  };

  // Normalize base64 data URIs for image/audio (same as ChatPage.jsx)
  const normalizeDataUri = (dataUri, mediaType) => {
    if (!dataUri || !dataUri.startsWith('data:')) return dataUri;
    if (dataUri.match(/^data:[a-z]+\/[a-z0-9+-]+;base64,/)) return dataUri;
    if (mediaType === 'audio' && dataUri.startsWith('data:audio;base64,')) {
      try {
        const base64Data = dataUri.substring(dataUri.indexOf(',') + 1);
        const binaryString = atob(base64Data.substring(0, 100));
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) return dataUri.replace('data:audio;base64,', 'data:audio/ogg;base64,');
        return dataUri.replace('data:audio;base64,', 'data:audio/ogg;base64,');
      } catch (e) { return dataUri.replace('data:audio;base64,', 'data:audio/ogg;base64,'); }
    }
    if (mediaType === 'image' && dataUri.startsWith('data:image;base64,')) return dataUri.replace('data:image;base64,', 'data:image/png;base64,');
    return dataUri;
  };

  // Render message content for preview: base64 image/audio as media, else truncated text (like ChatPage.jsx)
  const renderPreviewContent = (msg) => {
    const content = parseMessageContent(msg);
    if (!content) return null;
    const hasBase64Image = content.startsWith('data:image');
    const hasBase64Audio = content.startsWith('data:audio');
    const normalizedImg = hasBase64Image ? normalizeDataUri(content, 'image') : null;
    const normalizedAudio = hasBase64Audio ? normalizeDataUri(content, 'audio') : null;
    if (hasBase64Image && normalizedImg) {
      return <img src={normalizedImg} alt="Chat" className="rounded max-h-24 object-cover mt-1" />;
    }
    if (hasBase64Audio && normalizedAudio) {
      return <audio src={normalizedAudio} controls className="w-full mt-1 max-h-8" />;
    }
    const text = content.substring(0, 180);
    return <span>{text}{content.length > 180 ? '…' : ''}</span>;
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    navigate('/client-dashboard');
  };

  const handleChatClick = (client) => {
    setSelectedClient(client);
    navigate('/chat');
  };

  const handleFoodLogsClick = (client) => {
    setSelectedClient(client);
    navigate('/client-food-logs');
  };

  const handlePlanClick = (client) => {
    setSelectedClient(client);
    navigate('/client-meal-plans');
  };

  if (loading && clients.length === 0) {
    return <AdminDashboardSkeleton direction={direction} />;
  }

  if (error) {
    return (
      <div 
        className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-300 flex items-center justify-center p-4`}
        dir={direction}
      >
        <div className={`${themeClasses.bgCard}  rounded-lg p-6 sm:p-8 border ${themeClasses.borderPrimary} text-center w-full max-w-md`}>
          <p className={`text-red-500 text-base sm:text-lg mb-4`}>{t('admin.error')}: {error}</p>
          <div className="flex flex-col gap-2">
            <button 
              onClick={() => window.location.reload()} 
              className={`${themeClasses.btnPrimary} text-white font-semibold py-2 px-4 rounded-lg w-full`}
            >
              Retry
            </button>
            {(error.includes('profile') || error.includes('Profile') || error.includes('Sign in') || error.includes('Authorization')) && (
              <button
                type="button"
                onClick={async () => {
                  await authClient.signOut();
                  navigate('/', { replace: true });
                }}
                className={`w-full py-2 px-4 rounded-lg border ${themeClasses.borderPrimary} ${themeClasses.textPrimary} font-semibold`}
              >
                {language === 'hebrew' ? 'התנתק והתחבר מחדש' : 'Sign out and log in again'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- CLIENT LIST: tabs, next step, quick look ---
  const pulseTabs = [
    { id: 'all', label: t('admin.tab.all'), count: rosterAfterNonPulseFilters.length },
    { id: 'attention', label: t('admin.tab.attention'), count: rosterAfterNonPulseFilters.filter(needsAttention).length, dot: 'bg-amber-600' },
    { id: 'healthy', label: t('admin.tab.active'), count: filteredKpis.activeEngagement, dot: 'bg-forest' },
    { id: '24h', label: t('admin.tab.quiet'), count: filteredKpis.quietZone, dot: 'bg-amber-700' },
    { id: 'silent', label: t('admin.tab.silent'), count: filteredKpis.silentZone, dot: 'bg-red-700' },
    { id: 'onboarding', label: t('admin.tab.onboarding'), count: rosterAfterNonPulseFilters.filter((c) => c.engagementPulse === 'onboarding' || c.engagementPulse === 'new').length, dot: 'bg-[color:var(--progress)]' },
  ];
  const NEXT_DOT = { urgent: 'bg-red-600', attention: 'bg-amber-500', info: 'bg-[color:var(--progress)]', ok: 'bg-forest' };
  const nextStepLabel = (next, client) => {
    const label = t(`admin.next.${next.kind}`);
    if (next.kind === 'silent') return label.replace('{ago}', formatTimeAgo(client.lastInteractionTime));
    if (next.kind === 'stuck' || next.kind === 'onboarding') return label.replace('{n}', next.step || 0).replace('{total}', onboardingStepTotal);
    return label;
  };
  const onboardingLabel = (client) => {
    if (client.isOnboardingFinished) return t('admin.onboarding.completed');
    const st = client.onboardingStuckAt || client.lastStep;
    if (!st || !st.num) return t('admin.onboarding.notStarted');
    const name = st.key === 'payment' ? t('admin.onboarding.subscriptionPayment')
      : (st.key && t(`admin.onboarding.steps.${st.key}`) !== `admin.onboarding.steps.${st.key}` ? t(`admin.onboarding.steps.${st.key}`) : st.label);
    return `${t('admin.onboarding.step')} ${st.num}/${onboardingStepTotal}${name ? ` · ${name}` : ''}`;
  };
  const peekClient = peekClientId ? clients.find((c) => c.id === peekClientId) : null;
  // A click opens the quick look, unless the coach was selecting text to copy.
  const openPeek = (e, client) => {
    if (window.getSelection?.().toString()) return;
    setPeekClientId((id) => (id === client.id ? null : client.id));
  };

  return (
    <div
      className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-120 pb-8 font-ui`}
      dir={direction}
      data-playbook-dashboard-ready={loading ? undefined : 'true'}
      aria-busy={loading ? 'true' : undefined}
      >
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Header: what this page is, the month's income, and the two page actions. */}
        <PageHeader
          title={t('admin.list.title')}
          description={t('admin.list.desc')}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 ${isDarkMode ? 'border-line-dark' : 'border-line'}`} title={t('admin.kpi.expectedIncomeDesc')}>
                <span className={`text-[12px] ${themeClasses.textMuted}`}>{t('admin.income.label')}</span>
                <span className={`text-sm font-semibold tabular-nums ${themeClasses.textPrimary}`}>
                  {formatExpectedIncome(kpis.expectedIncomeThisMonth ?? 0, expectedIncomeCurrency)}
                </span>
                <button
                  type="button"
                  onClick={() => setExpectedIncomeCurrency((c) => (c === 'ILS' ? 'USD' : 'ILS'))}
                  aria-label={expectedIncomeCurrency === 'ILS' ? 'USD' : 'ILS'}
                  className={`rounded px-1 text-[11px] font-semibold ${themeClasses.textMuted} hover:text-forest`}
                >
                  {expectedIncomeCurrency === 'ILS' ? '$' : '₪'}
                </button>
              </div>
              <button
                type="button"
                onClick={loadDashboardData}
                disabled={loading}
                title={lastUpdated ? `${t('admin.lastUpdated')}: ${lastUpdated.toLocaleString(language === 'hebrew' ? 'he-IL' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })}` : t('admin.refresh')}
                aria-label={t('admin.refresh')}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors disabled:opacity-60 ${isDarkMode ? 'border-line-dark text-ink-muted-dark hover:text-white' : 'border-line text-ink-muted hover:text-ink'}`}
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                type="button"
                data-tour="playbook-referral"
                onClick={() => setReferralLinkDialogOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={referralLinkDialogOpen}
                title={t('admin.help.referralLink')}
                className={btnPrimary}
              >
                <LinkIcon size={15} className="shrink-0" />
                <span>{t('admin.invite')}</span>
              </button>
            </div>
          }
        />

        {/* Filter Bar — sticky; single Filter control opens drill-down panel */}
        <div data-tour="playbook-filters" className={`sticky top-0 z-10 ${cardBase} p-3 sm:p-4 mb-4`}>
          <div className="flex flex-col gap-3">
            <div role="tablist" aria-label={t('admin.table.engagementPulse')} data-tour="playbook-engagement" className="no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1">
              {pulseTabs.map((tab) => {
                const on = filterPulse === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setFilterPulse(tab.id)}
                    className={`inline-flex min-h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-[13px] font-semibold transition-colors ${
                      on
                        ? (isDarkMode ? 'bg-forest-faint-dark text-forest-ondark' : 'bg-forest-faint text-forest')
                        : (isDarkMode ? 'text-ink-muted-dark hover:bg-canvas-dark hover:text-white' : 'text-ink-muted hover:bg-canvas hover:text-ink')
                    }`}
                  >
                    {tab.dot && <span aria-hidden className={`h-2 w-2 rounded-full ${tab.dot}`} />}
                    {tab.label}
                    <span className={`rounded-full px-1.5 text-[11px] tabular-nums ${on ? '' : (isDarkMode ? 'bg-canvas-dark' : 'bg-canvas')}`}>{tab.count}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
              <div className="relative flex-1 min-w-0 group">
                <Search size={18} className={`absolute ${direction === 'rtl' ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-ink-muted group-focus-within:text-forest transition-colors pointer-events-none`} />
                <input
                  type="text"
                  placeholder={t('admin.filter.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${inputBase} ${direction === 'rtl' ? 'pr-11 pl-4' : 'pl-11 pr-4'}`}
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:shrink-0 sm:items-stretch sm:gap-2 sm:self-center w-full sm:w-auto">
              <div ref={filterPanelRef} className="relative w-full sm:w-auto">
                <button
                  type="button"
                  aria-expanded={filterPanelOpen}
                  aria-haspopup="dialog"
                  aria-controls="admin-dashboard-filter-panel"
                  onClick={() => {
                    setSortPanelOpen(false);
                    setSortPanelColumn(null);
                    setFilterPanelOpen((prev) => {
                      const next = !prev;
                      if (next) setFilterPanelCategory(null);
                      return next;
                    });
                  }}
                  className={`inline-flex w-full sm:w-auto items-center justify-center gap-2.5 px-4 py-2.5 sm:py-3 rounded-lg border text-sm font-semibold transition-all duration-200 ${
                    activeFilterCount > 0
                      ? isDarkMode
                        ? 'border-forest bg-forest-faint-dark text-forest-ondark hover:bg-forest-faint-dark/80'
                        : 'border-forest bg-forest-faint text-forest hover:bg-forest-faint'
                      : isDarkMode
                        ? 'border-line-dark bg-surface-dark text-ink-dark hover:bg-canvas-dark'
                        : 'border-line bg-surface text-ink hover:bg-canvas'
                  } focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark`}
                >
                  <svg className="h-5 w-5 shrink-0 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <path strokeLinecap="round" d="M4 6h16M7 12h10M10 18h4" />
                  </svg>
                  <span>{t('admin.filter.button')}</span>
                  {activeFilterCount > 0 && (
                    <span
                      className={`min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center text-xs font-bold tabular-nums ${
                        isDarkMode ? 'bg-forest text-white' : 'bg-forest text-white'
                      }`}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                {filterPanelOpen && (
                  <div
                    id="admin-dashboard-filter-panel"
                    role="dialog"
                    aria-label={t('admin.filter.panelTitle')}
                    dir={direction}
                    className={`absolute top-full z-30 mt-2 w-full min-w-[min(100%,18rem)] sm:min-w-[22rem] max-w-[min(22rem,calc(100vw-1.5rem))] end-0 overflow-hidden rounded-lg border border-forest/15 dark:border-forest/20 ${themeClasses.bgCard}  ring-1 ring-forest/10 dark:ring-forest/15 origin-top transition-[transform,opacity] duration-200 ease-out`}
                    style={{ maxHeight: 'min(70vh, 26rem)' }}
                  >
                    {!filterPanelCategory ? (
                      <div className="flex max-h-[min(70vh,26rem)] flex-col">
                        <div
                          className={`flex items-center justify-between gap-2 border-b px-4 py-3 ${themeClasses.borderPrimary} ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`}
                        >
                          <p className={`text-sm font-semibold ${themeClasses.textPrimary}`}>
                            {t('admin.filter.panelTitle')}
                          </p>
                          {activeFilterCount > 0 && (
                            <button
                              type="button"
                              onClick={() => clearAllAdminFilters()}
                              className={`text-xs font-medium transition-colors ${
                                isDarkMode
                                  ? 'text-forest-ondark hover:text-ink-dark'
                                  : 'text-forest hover:text-forest-hover'
                              }`}
                            >
                              {t('admin.filter.clearAll')}
                            </button>
                          )}
                        </div>
                        <ul className="max-h-[min(60vh,22rem)] overflow-y-auto overscroll-contain p-2">
                          {adminFilterCategories.map((cat) => {
                            const summary =
                              cat.options.find((o) => o.value === cat.value)?.label ?? '';
                            const isActive = cat.value !== 'all';
                            return (
                              <li key={cat.id}>
                                <button
                                  type="button"
                                  onClick={() => setFilterPanelCategory(cat.id)}
                                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-start transition-colors duration-150 ${
                                    isActive
                                      ? isDarkMode
                                        ? 'bg-forest-faint-dark hover:bg-forest-faint-dark/80'
                                        : 'bg-forest-faint hover:bg-forest-faint'
                                      : isDarkMode
                                        ? 'hover:bg-canvas-dark'
                                        : 'hover:bg-moss'
                                  }`}
                                >
                                  <span className={`min-w-0 flex-1 text-sm font-medium ${themeClasses.textPrimary}`}>
                                    {cat.title}
                                  </span>
                                  <span
                                    className={`max-w-[55%] truncate text-xs sm:text-sm ${
                                      isActive
                                        ? isDarkMode
                                          ? 'font-medium text-forest-ondark'
                                          : 'font-medium text-forest'
                                        : themeClasses.textMuted
                                    }`}
                                  >
                                    {summary}
                                  </span>
                                  <svg
                                    className={`h-5 w-5 shrink-0 opacity-45 ${direction === 'rtl' ? 'rotate-180' : ''}`}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    aria-hidden
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : (
                      (() => {
                        const cat = adminFilterCategories.find((c) => c.id === filterPanelCategory);
                        if (!cat) return null;
                        return (
                          <div className="flex max-h-[min(70vh,26rem)] flex-col">
                            <div
                              className={`flex items-center gap-1 border-b px-2 py-2 ${themeClasses.borderPrimary} ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`}
                            >
                              <button
                                type="button"
                                onClick={() => setFilterPanelCategory(null)}
                                className={`flex items-center gap-1.5 rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
                                  isDarkMode ? 'text-forest-ondark hover:bg-canvas-dark' : 'text-forest hover:bg-canvas'
                                }`}
                              >
                                <svg
                                  className={`h-5 w-5 shrink-0 ${direction === 'rtl' ? 'rotate-180' : ''}`}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  aria-hidden
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                                {t('admin.filter.back')}
                              </button>
                            </div>
                            <p className={`px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide ${themeClasses.textMuted}`}>
                              {cat.title}
                            </p>
                            <ul className="max-h-[min(58vh,20rem)] overflow-y-auto overscroll-contain px-2 pb-3 pt-1">
                              {cat.options.map((opt) => {
                                const selected = opt.value === cat.value;
                                return (
                                  <li key={`${cat.id}-${opt.value}`} className="mb-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        cat.setValue(opt.value);
                                        setFilterPanelCategory(null);
                                      }}
                                      className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-start text-sm transition-all duration-150 ${
                                        selected
                                          ? isDarkMode
                                            ? 'bg-forest-faint-dark font-semibold text-forest-ondark'
                                            : 'bg-forest-faint font-semibold text-forest'
                                          : isDarkMode
                                            ? `font-normal ${themeClasses.textPrimary} hover:bg-canvas-dark`
                                            : `font-normal ${themeClasses.textPrimary} hover:bg-moss`
                                      }`}
                                    >
                                      <span className="min-w-0 flex-1">{opt.label}</span>
                                      {selected && (
                                        <svg className="h-5 w-5 shrink-0 text-forest" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>

              <div ref={sortPanelRef} className="relative w-full sm:w-auto">
                <button
                  type="button"
                  aria-expanded={sortPanelOpen}
                  aria-haspopup="dialog"
                  aria-controls="admin-dashboard-sort-panel"
                  onClick={() => {
                    setFilterPanelOpen(false);
                    setFilterPanelCategory(null);
                    setSortPanelOpen((prev) => {
                      const next = !prev;
                      if (next) setSortPanelColumn(null);
                      return next;
                    });
                  }}
                  className={`inline-flex w-full sm:w-auto items-center justify-center gap-2.5 px-4 py-2.5 sm:py-3 rounded-lg border text-sm font-semibold transition-all duration-200 ${
                    sortBy
                      ? isDarkMode
                        ? 'border-forest bg-forest-faint-dark text-forest-ondark hover:bg-forest-faint-dark/80'
                        : 'border-forest bg-forest-faint text-forest hover:bg-forest-faint'
                      : isDarkMode
                        ? 'border-line-dark bg-surface-dark text-ink-dark hover:bg-canvas-dark'
                        : 'border-line bg-surface text-ink hover:bg-canvas'
                  } focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark`}
                >
                  <svg className="h-5 w-5 shrink-0 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <span>{t('admin.sort.by')}</span>
                  {sortBy ? (
                    <span
                      className={`min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center text-xs font-bold tabular-nums ${
                        isDarkMode ? 'bg-forest text-white' : 'bg-forest text-white'
                      }`}
                    >
                      1
                    </span>
                  ) : null}
                </button>
                {sortPanelOpen && (
                  <div
                    id="admin-dashboard-sort-panel"
                    role="dialog"
                    aria-label={t('admin.sort.panelTitle')}
                    dir={direction}
                    className={`absolute top-full z-30 mt-2 w-full min-w-[min(100%,18rem)] sm:min-w-[22rem] max-w-[min(22rem,calc(100vw-1.5rem))] end-0 overflow-hidden rounded-lg border border-forest/15 dark:border-forest/20 ${themeClasses.bgCard}  ring-1 ring-forest/10 dark:ring-forest/15 origin-top transition-[transform,opacity] duration-200 ease-out`}
                    style={{ maxHeight: 'min(70vh, 26rem)' }}
                  >
                    {!sortPanelColumn ? (
                      <div className="flex max-h-[min(70vh,26rem)] flex-col">
                        <div
                          className={`flex items-center justify-between gap-2 border-b px-4 py-3 ${themeClasses.borderPrimary} ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`}
                        >
                          <p className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{t('admin.sort.panelTitle')}</p>
                          {sortBy ? (
                            <button
                              type="button"
                              onClick={() => clearSort()}
                              className={`text-xs font-medium transition-colors ${
                                isDarkMode ? 'text-forest-ondark hover:text-ink-dark' : 'text-forest hover:text-forest-hover'
                              }`}
                            >
                              {t('admin.sort.clear')}
                            </button>
                          ) : null}
                        </div>
                        <ul className="max-h-[min(60vh,22rem)] overflow-y-auto overscroll-contain p-2">
                          {adminSortColumns.map((col) => {
                            const isColActive = sortBy === col.id;
                            const summary = isColActive
                              ? sortOrder === 'asc'
                                ? t('admin.sort.asc')
                                : t('admin.sort.desc')
                              : t('admin.sort.none');
                            return (
                              <li key={col.id}>
                                <button
                                  type="button"
                                  onClick={() => setSortPanelColumn(col.id)}
                                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-start transition-colors duration-150 ${
                                    isColActive
                                      ? isDarkMode
                                        ? 'bg-forest-faint-dark hover:bg-forest-faint-dark/80'
                                        : 'bg-forest-faint hover:bg-forest-faint'
                                      : isDarkMode
                                        ? 'hover:bg-canvas-dark'
                                        : 'hover:bg-moss'
                                  }`}
                                >
                                  <span className={`min-w-0 flex-1 text-sm font-medium ${themeClasses.textPrimary}`}>{col.title}</span>
                                  <span
                                    className={`max-w-[55%] truncate text-xs sm:text-sm ${
                                      isColActive
                                        ? isDarkMode
                                          ? 'font-medium text-forest-ondark'
                                          : 'font-medium text-forest'
                                        : themeClasses.textMuted
                                    }`}
                                  >
                                    {summary}
                                  </span>
                                  <svg
                                    className={`h-5 w-5 shrink-0 opacity-45 ${direction === 'rtl' ? 'rotate-180' : ''}`}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    aria-hidden
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : (
                      (() => {
                        const col = adminSortColumns.find((c) => c.id === sortPanelColumn);
                        if (!col) return null;
                        const directions = [
                          { order: 'asc', label: t('admin.sort.asc') },
                          { order: 'desc', label: t('admin.sort.desc') },
                        ];
                        return (
                          <div className="flex max-h-[min(70vh,26rem)] flex-col">
                            <div
                              className={`flex items-center gap-1 border-b px-2 py-2 ${themeClasses.borderPrimary} ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`}
                            >
                              <button
                                type="button"
                                onClick={() => setSortPanelColumn(null)}
                                className={`flex items-center gap-1.5 rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
                                  isDarkMode ? 'text-forest-ondark hover:bg-canvas-dark' : 'text-forest hover:bg-canvas'
                                }`}
                              >
                                <svg
                                  className={`h-5 w-5 shrink-0 ${direction === 'rtl' ? 'rotate-180' : ''}`}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  aria-hidden
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                                {t('admin.filter.back')}
                              </button>
                            </div>
                            <p className={`px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide ${themeClasses.textMuted}`}>
                              {col.title}
                            </p>
                            <p className={`px-4 pb-2 text-[11px] ${themeClasses.textMuted}`}>{t('admin.sort.direction')}</p>
                            <ul className="max-h-[min(58vh,20rem)] overflow-y-auto overscroll-contain px-2 pb-3 pt-1">
                              {directions.map(({ order, label }) => {
                                const selected = sortBy === col.id && sortOrder === order;
                                return (
                                  <li key={order} className="mb-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSortBy(col.id);
                                        setSortOrder(order);
                                        setSortPanelColumn(null);
                                      }}
                                      className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-start text-sm transition-all duration-150 ${
                                        selected
                                          ? isDarkMode
                                            ? 'bg-forest-faint-dark font-semibold text-forest-ondark'
                                            : 'bg-forest-faint font-semibold text-forest'
                                          : isDarkMode
                                            ? `font-normal ${themeClasses.textPrimary} hover:bg-canvas-dark`
                                            : `font-normal ${themeClasses.textPrimary} hover:bg-moss`
                                      }`}
                                    >
                                      <span className="min-w-0 flex-1">{label}</span>
                                      {selected ? (
                                        <svg className="h-5 w-5 shrink-0 text-forest" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      ) : null}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* The client list: one slim row per client. Click = quick look; double-click or "Open" = into the client. */}
        {(() => {
          const shown = filteredClients.slice(0, tableLimit);
          const cols = 'md:grid md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.1fr)_4.5rem_minmax(0,1.2fr)_minmax(0,1.8fr)_1.25rem] md:items-center md:gap-4';
          return (
            <div className={`${cardBase} overflow-hidden`}>
              <div className={`hidden ${cols} border-b px-4 py-2 text-[11px] font-medium ${themeClasses.textMuted} ${isDarkMode ? 'border-line-dark' : 'border-line'}`}>
                <span>{t('admin.col.client')}</span>
                <span>{t('admin.col.lastActive')}</span>
                <span>{t('admin.col.plans')}</span>
                <span>{t('admin.col.payment')}</span>
                <span>{t('admin.col.next')}</span>
                <span />
              </div>
              {shown.length === 0 && (
                <p className={`p-10 text-center text-sm ${themeClasses.textMuted}`}>{t('admin.noClients')}</p>
              )}
              <ul className={`divide-y ${isDarkMode ? 'divide-line-dark' : 'divide-line'}`}>
                {shown.map((client, index) => {
                  const isTourExampleRow = String(client.id) === DEMO_CLIENT_ID
                    || (index === 0 && !filteredClients.some((c) => String(c.id) === DEMO_CLIENT_ID));
                  const next = clientNextStep(client);
                  const selected = peekClientId === client.id;
                  const sub = client.subscription;
                  return (
                    <li key={client.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        data-tour={isTourExampleRow ? 'playbook-table-intel' : undefined}
                        aria-label={`${client.full_name || client.user_code || ''}: ${nextStepLabel(next, client)}`}
                        aria-pressed={selected}
                        onClick={(e) => openPeek(e, client)}
                        onDoubleClick={() => handleClientClick(client)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); setPeekClientId(client.id); }
                        }}
                        className={`group relative flex cursor-pointer items-center gap-3 px-4 py-3 outline-none transition-colors duration-120 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest ${cols} ${
                          selected
                            ? (isDarkMode ? 'bg-forest-faint-dark' : 'bg-forest-faint')
                            : (isDarkMode ? 'hover:bg-canvas-dark' : 'hover:bg-canvas')
                        }`}
                      >
                        {/* Client */}
                        <div className="flex min-w-0 flex-1 items-center gap-3" data-tour={isTourExampleRow ? 'playbook-open-client' : undefined}>
                          <ClientAvatar
                            client={client}
                            size="sm"
                            pulseDotClass={getPulseDotColor(client.engagementPulse)}
                            dotBorderClass={isDarkMode ? 'border-surface-dark' : 'border-surface'}
                          />
                          <div className="min-w-0">
                            <div className={`truncate text-sm font-semibold ${themeClasses.textPrimary}`}>{client.full_name || client.user_code || '-'}</div>
                            <div className={`truncate text-[12px] ${themeClasses.textMuted}`}>
                              <span className="md:hidden">{nextStepLabel(next, client)}</span>
                              <span className="hidden md:inline">{client.phone_number || client.email || client.user_code || ''}</span>
                            </div>
                          </div>
                        </div>

                        {/* Last active */}
                        <div className={`shrink-0 text-end text-[12px] md:text-start md:text-[13px] ${themeClasses.textSecondary}`}>
                          <span className={`block ${getPulseColor(client.engagementPulse)} md:!font-normal`}>
                            {client.lastInteractionTime ? formatTimeAgo(client.lastInteractionTime) : t('admin.pulse.never')}
                          </span>
                          {client.lastInteractionType && formatInteractionType(client.lastInteractionType) && (
                            <span className={`hidden text-[11px] md:block ${themeClasses.textMuted}`}>{formatInteractionType(client.lastInteractionType)}</span>
                          )}
                        </div>

                        {/* Plans: two icons, coloured only when active */}
                        <div className="hidden items-center gap-2 md:flex">
                          {[
                            { Icon: Utensils, status: client.mealPlanStatus, label: t('admin.filter.mealPlan') },
                            { Icon: Dumbbell, status: client.workoutPlanStatus, label: t('admin.filter.workoutPlan') },
                          ].map(({ Icon, status, label }) => (
                            <span key={label} title={`${label}: ${t(`admin.status.${resolvePlanStatus(status)}`)}`}
                              className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${
                                status === 'active'
                                  ? 'bg-forest-faint text-forest dark:bg-forest-faint-dark dark:text-forest-ondark'
                                  : status === 'paused'
                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400'
                                    : `${themeClasses.textMuted} opacity-40`
                              }`}>
                              <Icon size={14} aria-hidden />
                              <span className="sr-only">{label}: {t(`admin.status.${resolvePlanStatus(status)}`)}</span>
                            </span>
                          ))}
                        </div>

                        {/* Payment */}
                        <div className="hidden min-w-0 text-[13px] md:block">
                          {sub ? (
                            <div className="flex min-w-0 items-center gap-1.5">
                              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${getSubscriptionStatusColor(sub.status)}`}>{sub.status || '—'}</span>
                              {sub.amount_total != null && <span className={`truncate font-medium tabular-nums ${themeClasses.textPrimary}`}>{formatSubscriptionAmount(sub)}</span>}
                            </div>
                          ) : (
                            <span className={themeClasses.textMuted}>{t('admin.subscription.none')}</span>
                          )}
                        </div>

                        {/* Next step */}
                        <div className="hidden min-w-0 items-center gap-2 md:flex">
                          <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${NEXT_DOT[next.level]}`} />
                          <span className={`truncate text-[13px] ${next.level === 'ok' ? themeClasses.textMuted : themeClasses.textPrimary}`}>{nextStepLabel(next, client)}</span>
                        </div>

                        <ChevronRight
                          size={16}
                          aria-hidden
                          className={`hidden shrink-0 transition-transform group-hover:translate-x-0.5 md:block ${themeClasses.textMuted} ${direction === 'rtl' ? 'rotate-180 group-hover:-translate-x-0.5' : ''}`}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              {filteredClients.length > tableLimit && (
                <div className={`flex justify-center border-t p-3 ${isDarkMode ? 'border-line-dark' : 'border-line'}`}>
                  <button type="button" onClick={() => setTableLimit((prev) => prev + 10)} className={btnSecondary}>
                    <ChevronDown size={16} aria-hidden /> {t('admin.table.load10More')}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
        {filteredClients.length > 0 && (
          <p className={`mt-2 text-center text-[12px] ${themeClasses.textMuted}`}>{t('admin.peek.hint')}</p>
        )}
      </main>

      {/* Quick look: everything about one client, without leaving the list. */}
      {peekClient && createPortal(
        <div className="fixed inset-0 z-[100] flex justify-end font-ui" dir={direction} role="dialog" aria-modal="true" aria-labelledby="peek-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setPeekClientId(null); }}>
          <button type="button" aria-label={t('admin.peek.close')} onClick={() => setPeekClientId(null)} className="absolute inset-0 cursor-default bg-black/20 dark:bg-black/40" />
          {(() => {
            const c = peekClient;
            const next = clientNextStep(c);
            const sub = c.subscription;
            const w = getWeightMeasurementStatus(c);
            const messages = (c.chatPreview || []).slice(-3).reverse();
            const section = `border-t px-5 py-4 ${isDarkMode ? 'border-line-dark' : 'border-line'}`;
            const label = `mb-1.5 text-[11px] font-medium ${themeClasses.textMuted}`;
            const quick = `inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 text-[13px] font-semibold transition-colors ${isDarkMode ? 'border-line-dark text-ink-dark hover:bg-canvas-dark' : 'border-line text-ink hover:bg-canvas'}`;
            return (
              <div data-tour="playbook-quick-look" className={`relative flex h-full w-full max-w-[420px] flex-col border-s shadow-xl ${isDarkMode ? 'border-line-dark bg-surface-dark' : 'border-line bg-surface'}`}>
                {/* Who, and the way in */}
                <div className="px-5 pb-4 pt-5">
                  <div className="flex items-start gap-3">
                    <ClientAvatar client={c} size="md" pulseDotClass={getPulseDotColor(c.engagementPulse)} dotBorderClass={isDarkMode ? 'border-surface-dark' : 'border-surface'} />
                    <div className="min-w-0 flex-1">
                      <h2 id="peek-title" className={`truncate text-base font-semibold ${themeClasses.textPrimary}`}>{c.full_name || c.user_code}</h2>
                      <p className={`truncate text-[12px] ${themeClasses.textMuted}`}>{[c.phone_number, c.email].filter(Boolean).join(' · ') || c.user_code}</p>
                      <p className={`mt-0.5 text-[12px] ${getPulseColor(c.engagementPulse)}`}>{t(`admin.pulse.${c.engagementPulse}`)}</p>
                    </div>
                    <button type="button" autoFocus onClick={() => setPeekClientId(null)} aria-label={t('admin.peek.close')} className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${themeClasses.textMuted} hover:bg-canvas dark:hover:bg-canvas-dark`}>
                      <X size={18} aria-hidden />
                    </button>
                  </div>
                  <button type="button" data-tour="playbook-action-view" onClick={() => handleClientClick(c)} className={`${btnPrimary} mt-4 w-full justify-center`}>
                    {t('admin.peek.open')}
                    <ChevronRight size={16} aria-hidden className={direction === 'rtl' ? 'rotate-180' : ''} />
                  </button>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={() => handleChatClick(c)} className={quick}><MessageCircle size={15} aria-hidden /> {t('admin.action.chat')}</button>
                    <button type="button" onClick={() => handlePlanClick(c)} className={quick}><FileText size={15} aria-hidden /> {t('admin.action.plan')}</button>
                    <button type="button" onClick={() => handleFoodLogsClick(c)} className={quick}><Camera size={15} aria-hidden /> {language === 'hebrew' ? 'יומן' : 'Food'}</button>
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                  {/* What to do next */}
                  <div className={section}>
                    <p className={label}>{t('admin.col.next')}</p>
                    <p className={`flex items-center gap-2 text-sm font-medium ${themeClasses.textPrimary}`}>
                      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${NEXT_DOT[next.level]}`} />
                      {nextStepLabel(next, c)}
                    </p>
                  </div>

                  {/* Facts */}
                  <dl className={`${section} grid grid-cols-2 gap-x-4 gap-y-3 text-[13px]`}>
                    <div>
                      <dt className={label}>{t('admin.col.lastActive')}</dt>
                      <dd className={themeClasses.textPrimary}>
                        {c.lastInteractionTime ? formatTimeAgo(c.lastInteractionTime) : t('admin.pulse.never')}
                        {c.lastInteractionType && formatInteractionType(c.lastInteractionType) && <span className={`ms-1 ${themeClasses.textMuted}`}>{formatInteractionType(c.lastInteractionType)}</span>}
                      </dd>
                    </div>
                    <div>
                      <dt className={label}>{t('admin.table.weightMeasurement')}</dt>
                      <dd className={w.status === 'missing_week' ? 'text-amber-700 dark:text-amber-400' : themeClasses.textPrimary}>{w.label}</dd>
                    </div>
                    <div>
                      <dt className={label}>{t('admin.filter.mealPlan')}</dt>
                      <dd className="flex flex-wrap items-center gap-1.5"><StatusPill status={c.mealPlanStatus} />{c.mealPlanStatusUpdated && c.mealPlanStatus && <span className={`text-[11px] ${themeClasses.textMuted}`}>{formatTimeAgo(c.mealPlanStatusUpdated)}</span>}</dd>
                    </div>
                    <div>
                      <dt className={label}>{t('admin.filter.workoutPlan')}</dt>
                      <dd><StatusPill status={c.workoutPlanStatus} /></dd>
                    </div>
                    <div className="col-span-2">
                      <dt className={label}>{t('admin.table.onboardingProgress')}</dt>
                      <dd className={themeClasses.textPrimary}>{onboardingLabel(c)}</dd>
                    </div>
                  </dl>

                  {/* Recent messages */}
                  <div className={section}>
                    <p className={label}>{t('admin.peek.lastMessages')}</p>
                    {messages.length === 0 ? (
                      <p className={`text-[13px] ${themeClasses.textMuted}`}>{t('admin.chat.noMessages')}</p>
                    ) : (
                      <ul className="space-y-2">
                        {messages.map((m, i) => (
                          <li key={m.id || i} className={`rounded-lg px-3 py-2 text-[13px] ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`}>
                            <div className={`mb-0.5 text-[11px] ${themeClasses.textMuted}`}>{m.created_at ? formatTimeAgo(m.created_at) : ''}</div>
                            <div className={`break-words ${themeClasses.textPrimary}`}>{renderPreviewContent(m)}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Payment */}
                  <div className={section}>
                    <p className={label}>{t('admin.table.subscription')}</p>
                    {sub ? (
                      <div className="space-y-1 text-[13px]">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${getSubscriptionStatusColor(sub.status)}`}>{sub.status || '—'}</span>
                          {sub.subscription_type && <span className={themeClasses.textMuted}>{sub.subscription_type}</span>}
                          {sub.amount_total != null && <span className={`font-semibold tabular-nums ${themeClasses.textPrimary}`}>{formatSubscriptionAmount(sub)}</span>}
                        </div>
                        <p className={themeClasses.textMuted}>{t('admin.table.periodEnd')}: {formatSubscriptionDate(sub.current_period_end) ?? '—'}</p>
                        {sub.cancel_at_period_end && <p className="text-amber-700 dark:text-amber-400">{t('admin.subscription.cancelAtPeriodEnd')}</p>}
                        {sub.stripe_customer_id && (
                          <button type="button" onClick={() => { setPeekClientId(null); openReceiptsModal(sub.stripe_customer_id); }} className="font-medium text-forest hover:underline dark:text-forest-ondark">
                            {t('admin.subscription.receipts')} ↓
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className={`text-[13px] ${themeClasses.textMuted}`}>{t('admin.subscription.none')}</p>
                    )}
                  </div>

                  {/* Who handles them (admins only) */}
                  {showClientAssignmentsColumn && (
                    <div className={section}>
                      <p className={label}>{t('admin.table.clientAssignments')}</p>
                      {c.provider_id && !assignableProviders.some((p) => p.id === c.provider_id) ? (
                        <p className={`text-[13px] font-medium ${themeClasses.textPrimary}`}>{c.provider_name || c.provider_id}</p>
                      ) : (
                        <select
                          value={c.provider_id || ''}
                          onChange={(e) => handleAssignProvider(c.user_code, e.target.value || null)}
                          disabled={!!updatingProviderFor[c.user_code]}
                          className={`w-full rounded-lg border px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-forest disabled:opacity-50 ${isDarkMode ? 'border-line-dark bg-canvas-dark text-white' : 'border-line bg-canvas text-ink'}`}
                        >
                          <option value="">—</option>
                          {assignableProviders.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Account actions, out of the way */}
                  <div className={`${section} flex flex-wrap gap-2`}>
                    <button
                      type="button"
                      onClick={() => handleToggleClientBlock(c)}
                      disabled={!!updatingBlockFor[c.user_code]}
                      title={t('admin.help.blockClientAction')}
                      className={`inline-flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium disabled:opacity-60 ${c.is_blocked ? 'text-forest hover:bg-forest-faint dark:text-forest-ondark dark:hover:bg-forest-faint-dark' : 'text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20'}`}
                    >
                      <Ban size={15} aria-hidden /> {c.is_blocked ? t('admin.action.unblockClient') : t('admin.action.blockClient')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { setPeekClientId(null); handleDeleteClientClick(e, c); }}
                      className="inline-flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <Trash2 size={15} aria-hidden /> {t('admin.action.delete')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>,
        document.body,
      )}

      {/* Receipts modal */}
      {receiptsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-canvas-dark/60  animate-in fade-in" onClick={closeReceiptsModal}>
          <div
            className={`${themeClasses.bgCard} rounded-lg  border border-line dark:border-line-dark max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col `}
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-line dark:border-line-dark bg-canvas/50 dark:bg-canvas-dark/50 flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${themeClasses.textPrimary}`}>{t('admin.receipts.title')}</h3>
              <button type="button" onClick={closeReceiptsModal} className="p-2 rounded-lg text-ink-muted hover:text-ink-muted dark:hover:text-ink-dark hover:bg-canvas dark:hover:bg-surface-dark transition-colors" aria-label={t('admin.receipts.close')}>
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {receiptsModalLoading && (
                <p className={`${themeClasses.textMuted} text-sm`}>{t('admin.receipts.loading')}</p>
              )}
              {!receiptsModalLoading && receiptsModalData?.error && (
                <p className="text-red-500 text-sm">{receiptsModalData.error}</p>
              )}
              {!receiptsModalLoading && receiptsModalData && !receiptsModalData.error && (
                <>
                  <p className={`text-sm font-medium ${themeClasses.textPrimary} mb-4 pb-3 border-b ${themeClasses.borderPrimary}`}>
                    {t('admin.receipts.totalPaid')}: {receiptsModalData.currency?.toUpperCase() === 'ILS' ? '₪' : '$'}{Number(receiptsModalData.totalPaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {receiptsModalData.currency?.toUpperCase() || 'USD'}
                  </p>
                  {receiptsModalData.receipts && receiptsModalData.receipts.length > 0 ? (
                    <table className="w-full table-fixed border-collapse text-sm">
                      <thead>
                        <tr className={`border-b ${themeClasses.borderPrimary}`}>
                          <th className={`py-2.5 pr-3 text-left w-[32%] ${themeClasses.textSecondary} font-medium`}>{t('admin.receipts.date')}</th>
                          <th className={`py-2.5 pr-3 text-right w-[28%] ${themeClasses.textSecondary} font-medium`}>{t('admin.receipts.amount')}</th>
                          <th className={`py-2.5 text-right w-[40%] ${themeClasses.textSecondary} font-medium`}>{t('admin.receipts.download')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receiptsModalData.receipts.map((r, i) => (
                          <tr key={i} className={`border-b ${themeClasses.borderPrimary}`}>
                            <td className="py-2.5 pr-3 align-middle">{r.date}</td>
                            <td className={`py-2.5 pr-3 text-right tabular-nums ${themeClasses.textPrimary}`}>{r.currency} {Number(r.amount).toFixed(2)}</td>
                            <td className="py-2.5 text-right align-middle">
                              <span className="inline-flex gap-3">
                                {r.receiptUrl && (
                                  <a href={r.receiptUrl} target="_blank" rel="noopener noreferrer" className={`text-forest dark:text-forest-ondark hover:underline text-xs font-medium inline-flex items-center gap-1`}>
                                    <Eye size={14} /> {t('admin.receipts.view')}
                                  </a>
                                )}
                                {r.invoicePdf && (
                                  <a href={r.invoicePdf} target="_blank" rel="noopener noreferrer" className={`text-forest dark:text-forest-ondark hover:underline text-xs font-medium inline-flex items-center gap-1`}>
                                    <FileText size={14} /> {t('admin.receipts.pdf')}
                                  </a>
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className={`${themeClasses.textMuted} text-sm`}>{t('admin.receipts.noReceipts')}</p>
                  )}
                </>
              )}
            </div>
            <div className={`px-4 py-3 border-t ${themeClasses.borderPrimary} flex justify-end`}>
              <button type="button" onClick={closeReceiptsModal} className={btnPrimary}>
                {t('admin.receipts.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Client Confirmation Modal */}
      {clientToDelete && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-delete-modal-title"
          onClick={closeDeleteModal}
        >
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-canvas-dark  bg-opacity-80" aria-hidden="true" />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div
              className={`inline-block align-bottom ${themeClasses.bgCard} rounded-lg text-left overflow-hidden  transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full max-w-[calc(100vw-1rem)] border-2 border-red-500/50`}
              onClick={(e) => e.stopPropagation()}
              dir={direction}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center text-red-600 dark:text-red-400">
                    <AlertCircle size={24} strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold ${themeClasses.textPrimary} mb-2`} id="admin-delete-modal-title">
                      {t('admin.delete.title')}
                    </h3>
                    <p className={`${themeClasses.textSecondary} font-semibold mb-2`}>
                      {clientToDelete.full_name || clientToDelete.email || clientToDelete.user_code || 'Unknown'}
                    </p>
                    <p className="text-red-500 dark:text-red-400 font-bold text-base mb-4">
                      {t('admin.delete.warning')}
                    </p>
                    <p className={`${themeClasses.textSecondary} text-sm font-medium mb-2`}>{t('admin.delete.willDelete')}</p>
                    <ul className={`${themeClasses.textSecondary} text-sm space-y-1 mb-4 list-disc list-inside`}>
                      <li>{t('admin.delete.mealPlans')}</li>
                      <li>{t('admin.delete.messages')}</li>
                      <li>{t('admin.delete.conversations')}</li>
                      <li>{t('admin.delete.clientProfile')}</li>
                    </ul>
                    <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                      {t('admin.delete.confirm')} {language === 'hebrew' ? '(מחק)' : '(DELETE)'}
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={language === 'hebrew' ? 'מחק' : 'DELETE'}
                      className={`w-full px-4 py-3 rounded-lg border-2 focus:ring-2 focus:ring-red-500 outline-none text-base ${
                        deleteConfirmText.trim() === (language === 'hebrew' ? 'מחק' : 'DELETE')
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-line dark:border-line-dark'
                      } ${isDarkMode ? 'bg-surface-dark text-white' : 'bg-white text-canvas-dark'}`}
                    />
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
                  <button
                    onClick={closeDeleteModal}
                    disabled={isDeleting}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium border ${themeClasses.borderPrimary} ${themeClasses.textPrimary} hover:opacity-90 disabled:opacity-50`}
                  >
                    {t('admin.delete.cancel')}
                  </button>
                  <button
                    onClick={handleDeleteClientConfirm}
                    disabled={isDeleting || deleteConfirmText.trim() !== (language === 'hebrew' ? 'מחק' : 'DELETE')}
                    className="flex-1 px-4 py-3 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? (language === 'hebrew' ? 'מוחק...' : 'Deleting...') : t('admin.delete.submit')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral Link — slide-in side panel */}
      {referralLinkDialogOpen && (
        <div
          className="fixed inset-0 z-[60]"
          aria-labelledby="referral-modal-title"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/55 cursor-pointer transition-opacity duration-200 ease-out"
            aria-label={language === 'hebrew' ? 'סגור' : 'Close'}
            onClick={() => setReferralLinkDialogOpen(false)}
          />

          <aside
            data-tour="playbook-referral-dialog"
            className={`referral-drawer-${direction === 'rtl' ? 'rtl' : 'ltr'} absolute inset-y-0 end-0 flex w-full max-w-md sm:max-w-lg flex-col border-s border-line dark:border-line-dark bg-surface dark:bg-surface-dark`}
            dir={direction}
          >
            {/* Header */}
            <div className="shrink-0 px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between gap-3 border-b border-line dark:border-line-dark">
              <h3 className={`text-[18px] font-semibold leading-tight ${themeClasses.textPrimary}`} id="referral-modal-title">
                {t('admin.referral.title')}
              </h3>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setReferralLinkDialogOpen(false)}
                  className={`inline-flex items-center justify-center min-h-11 min-w-11 rounded-md transition-colors duration-120 ${themeClasses.textMuted} hover:bg-moss dark:hover:bg-moss-dark hover:text-ink dark:hover:text-ink-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2`}
                  aria-label={language === 'hebrew' ? 'סגור' : 'Close'}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Tabs — three invite methods */}
            <div className="shrink-0 px-4 py-3 sm:px-5 border-b border-line dark:border-line-dark">
              <div
                data-tour="playbook-referral-tabs"
                className="flex p-1 rounded-md gap-1 border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark"
                role="tablist"
              >
                {[
                  { id: 'simple', label: t('admin.referral.simple'), icon: <LinkIcon size={16} /> },
                  { id: 'limited', label: t('admin.referral.limited'), icon: <Clock size={16} /> },
                  { id: 'onboarding', label: t('admin.referral.generateCodeTab'), icon: <FileText size={16} /> },
                ].map((tab) => {
                  const isActive = linkType === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setLinkType(tab.id)}
                      className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 min-h-11 py-2 px-2 text-[13px] font-semibold rounded-sm transition-colors duration-120 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest ${
                        isActive
                          ? 'bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark dark:text-forest-ondark'
                          : 'text-ink-muted dark:text-ink-muted-dark hover:text-ink dark:hover:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark'
                      }`}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 sm:p-5">
                {userRole === 'sys_admin' ? (
                  <div className={`mb-6 space-y-2 rounded-lg border ${isDarkMode ? 'border-line-dark bg-surface-dark/30' : 'border-line bg-canvas/50'} p-4`}>
                    <label className={`block text-xs sm:text-sm font-semibold uppercase tracking-wider ${themeClasses.textSecondary}`} htmlFor="referral-company-select">
                      {t('admin.referral.targetCompany')}
                    </label>
                    <div className="relative" ref={referralCompanyMenuRef}>
                      <button
                        type="button"
                        id="referral-company-select"
                        disabled={referralCompaniesLoading}
                        aria-haspopup="listbox"
                        aria-expanded={referralCompanyMenuOpen}
                        onClick={() => !referralCompaniesLoading && setReferralCompanyMenuOpen((open) => !open)}
                        className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg border transition-all duration-300 text-sm text-start font-medium outline-none focus:ring-2 focus:ring-forest ${referralCompanyMenuOpen ? 'border-forest ' : isDarkMode ? 'border-line-dark bg-canvas-dark/50 hover:border-line-dark' : 'border-line bg-white hover:border-ink-muted'} disabled:opacity-60`}
                      >
                        <span className={`truncate min-w-0 ${themeClasses.textPrimary}`}>{referralCompanySelectLabel}</span>
                        <ChevronDown
                          size={18}
                          className={`shrink-0 ${themeClasses.textMuted} transition-transform duration-300 ${referralCompanyMenuOpen ? 'rotate-180 text-forest' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                      {referralCompanyMenuOpen ? (
                      <ul
                        role="listbox"
                        aria-labelledby="referral-company-select"
                        className={`absolute z-[60] left-0 right-0 mt-2 max-h-56 overflow-y-auto rounded-lg border py-1.5 animate-in fade-in custom-scrollbar ${
                          isDarkMode
                            ? 'bg-surface-dark border-line-dark '
                            : 'bg-white border-line '
                        }`}
                      >
                          <li role="none">
                            <button
                              type="button"
                              role="option"
                              aria-selected={!referralTargetCompanyId}
                              onClick={() => {
                                setReferralTargetCompanyId('');
                                setReferralCompanyMenuOpen(false);
                              }}
                              className={`w-full text-start px-4 py-2.5 text-sm transition-colors font-medium ${
                                !referralTargetCompanyId
                                  ? 'bg-forest/10 text-forest dark:text-forest-ondark'
                                  : isDarkMode
                                    ? 'text-ink-muted-dark hover:bg-moss-dark/50 hover:text-white'
                                    : 'text-ink-muted hover:bg-canvas hover:text-ink'
                              }`}
                            >
                              {t('admin.referral.personalAccount')}
                            </button>
                          </li>
                          {referralCompanies.map((c) => {
                            const selected = String(referralTargetCompanyId) === String(c.id);
                            return (
                              <li key={c.id} role="none">
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={selected}
                                  onClick={() => {
                                    setReferralTargetCompanyId(String(c.id));
                                    setReferralCompanyMenuOpen(false);
                                  }}
                                  className={`w-full text-start px-4 py-2.5 text-sm transition-colors font-medium ${
                                    selected
                                      ? 'bg-forest/10 text-forest dark:text-forest-ondark'
                                      : isDarkMode
                                        ? 'text-ink-muted-dark hover:bg-moss-dark/50 hover:text-white'
                                        : 'text-ink-muted hover:bg-canvas hover:text-ink'
                                  }`}
                                >
                                  {c.name}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </div>
                    {referralCompaniesLoading ? (
                      <p className={`text-xs ${themeClasses.textMuted} flex items-center gap-1.5 mt-2`}><RefreshCw size={12} className="animate-spin" /> {t('admin.referral.loadingCompanies')}</p>
                    ) : null}
                    {referralTargetCompanyId && referralManagerLoading ? (
                      <p className={`text-xs ${themeClasses.textMuted} flex items-center gap-1.5 mt-2`}><RefreshCw size={12} className="animate-spin" /> {t('admin.referral.resolvingManager')}</p>
                    ) : null}
                    {referralTargetCompanyId && referralManagerError ? (
                      <p className="text-xs text-red-500 dark:text-red-400 mt-2 flex items-center gap-1.5"><AlertCircle size={14} /> {referralManagerError}</p>
                    ) : null}
                  </div>
                ) : null}
                {linkType === 'simple' ? (
                  <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-200">
                    <div className="text-center space-y-3 pt-2">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark mb-2 border border-line dark:border-line-dark">
                        <LinkIcon size={28} />
                      </div>
                      <h4 className={`text-xl font-medium ${themeClasses.textPrimary}`}>
                        {language === 'hebrew' ? 'הקישור הראשי שלך' : 'Your Main Referral Link'}
                      </h4>
                      <p className={`${themeClasses.textMuted} text-sm max-w-sm mx-auto leading-relaxed`}>
                        {t('admin.referral.simpleDesc')}
                      </p>
                    </div>

                    {(userRole === 'sys_admin' || userRole === 'company_manager') && (
                      <div className="max-w-xl mx-auto rounded-lg border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark p-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className={`text-[13px] font-semibold ${themeClasses.textPrimary} inline-flex items-center gap-2`}>
                            <LayoutTemplate size={16} className="text-forest-ondark shrink-0" aria-hidden />
                            {t('admin.referral.landingPage')}
                          </p>
                          <p className={`text-[11px] mt-0.5 ${themeClasses.textMuted}`}>{t('admin.referral.editLandingHint')}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setReferralLinkDialogOpen(false);
                            navigate('/landing-page-builder');
                          }}
                          className={`${btnSecondary} shrink-0`}
                        >
                          {t('admin.referral.editLanding')}
                        </button>
                      </div>
                    )}
                    
                    <div className="max-w-xl mx-auto space-y-8">
                      <div className={`p-1.5 rounded-lg flex items-center gap-2 border transition-all duration-300 focus-within:ring-4 focus-within:ring-forest/20 ${isDarkMode ? 'bg-canvas-dark/80 border-line-dark ' : 'bg-canvas border-line shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)]'}`}>
                        <input
                          type="text"
                          readOnly
                          value={generateClientReferralLink()}
                          className={`flex-1 bg-transparent px-4 py-3 outline-none text-sm font-semibold ${themeClasses.textPrimary} truncate`}
                        />
                        <button
                          type="button"
                          onClick={() => handleCopyReferralLink()}
                          disabled={!effectiveReferralManagerId}
                          className="shrink-0 flex items-center gap-2 bg-forest hover:bg-forest-hover text-white font-bold py-3 px-5 rounded-lg transition-all shadow-md shadow-forest/20 active:scale-95 disabled:opacity-50 disabled:grayscale"
                        >
                          <span className="hidden sm:inline">{t('admin.referral.copy')}</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                        </button>
                      </div>

                      <div className="flex flex-col items-center pb-4">
                        <div ref={qrCodeRef} className={`relative p-6 sm:p-8 rounded-[2rem] ${isDarkMode ? 'bg-white' : 'bg-white shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] border border-canvas'} group transition-transform hover:scale-[1.02] duration-500`}>
                          <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-forest rounded-tl-3xl opacity-0 group-hover:opacity-100 transition-all duration-500 -translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                          <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-forest rounded-tr-3xl opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                          <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-forest rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-all duration-500 -translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                          <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-forest rounded-br-3xl opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                          
                          <SimpleQRCode value={generateClientReferralLink() || ' '} size={180} />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopyQRCodeImage()}
                          disabled={!effectiveReferralManagerId}
                          className={`mt-8 px-6 py-3 rounded-lg font-bold text-sm border flex items-center gap-2 transition-all active:scale-95  ${isDarkMode ? 'border-line-dark bg-surface-dark text-ink-dark hover:bg-moss-dark hover:border-line-dark' : 'border-line bg-white text-ink-muted hover:bg-canvas  hover:border-line'}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                          {t('admin.referral.copyQR')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : linkType === 'onboarding' ? (
                  <div className="space-y-5 sm:space-y-6">
                    <div>
                      <p className={`${themeClasses.textMuted} text-xs sm:text-sm mb-3 sm:mb-4`}>{t('admin.referral.generateCodeDesc')}</p>
                      <div className={`rounded-lg border ${themeClasses.borderPrimary} p-3 sm:p-4 mb-4 space-y-3`}>
                        <div>
                          <span className={`${themeClasses.textSecondary} text-xs sm:text-sm font-medium`}>{t('admin.referral.accessCode')}</span>
                          <p className={`${themeClasses.textMuted} text-xs mt-1`}>{t('admin.referral.onboardingCodeHelp')}</p>
                        </div>
                        <div className="space-y-2">
                          <label className={`${themeClasses.textSecondary} text-xs sm:text-sm font-medium block`} htmlFor="onboarding-max-slots">
                            {t('admin.referral.onboardingMaxSlots')}
                          </label>
                          <p className={`${themeClasses.textMuted} text-xs`}>{t('admin.referral.onboardingMaxSlotsHelp')}</p>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
                            <input
                              id="onboarding-max-slots"
                              type="number"
                              min={1}
                              max={100000}
                              disabled={onboardingCodeUnlimitedSlots}
                              value={onboardingCodeUnlimitedSlots ? '' : onboardingCodeSlotCount}
                              onChange={(e) => setOnboardingCodeSlotCount(e.target.value === '' ? 1 : e.target.value)}
                              className={`w-full sm:w-32 px-3 py-2 rounded-lg border ${themeClasses.borderPrimary} ${themeClasses.bgPrimary} ${themeClasses.textPrimary} focus:ring-2 focus:ring-forest outline-none text-base disabled:opacity-50`}
                            />
                            <label className={`flex items-center gap-2 cursor-pointer select-none ${themeClasses.textSecondary} text-sm`}>
                              <input
                                type="checkbox"
                                checked={onboardingCodeUnlimitedSlots}
                                onChange={(e) => setOnboardingCodeUnlimitedSlots(e.target.checked)}
                                className="rounded border-ink-muted text-forest focus:ring-forest"
                              />
                              {t('admin.referral.unlimitedSlots')}
                            </label>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={handleGenerateOnboardingAccessCode}
                            disabled={generatingOnboardingCode || !effectiveReferralManagerId}
                            className={`${themeClasses.btnPrimary} text-white font-semibold py-2 px-4 rounded-lg transition-all touch-manipulation disabled:opacity-50 text-sm`}
                          >
                            {generatingOnboardingCode ? (language === 'hebrew' ? 'יוצר…' : 'Generating…') : t('admin.referral.generateCode')}
                          </button>
                          {referralOnboardingCode ? (
                            <>
                              <button
                                type="button"
                                onClick={handleCopyOnboardingCode}
                                className={`py-2 px-4 rounded-lg border ${themeClasses.borderPrimary} ${themeClasses.textPrimary} font-medium text-sm touch-manipulation hover:opacity-90`}
                              >
                                {t('admin.referral.copyCode')}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setReferralOnboardingCode('');
                                  setReferralOnboardingMaxUses(null);
                                }}
                                className={`py-2 px-4 rounded-lg border ${themeClasses.borderPrimary} ${themeClasses.textPrimary} font-medium text-sm touch-manipulation hover:opacity-90`}
                              >
                                {t('admin.referral.clearCode')}
                              </button>
                            </>
                          ) : null}
                        </div>
                        {referralOnboardingCode ? (
                          <div className="space-y-1">
                            <p className={`font-mono text-lg sm:text-xl tracking-widest ${themeClasses.textPrimary}`} dir="ltr">
                              {referralOnboardingCode}
                            </p>
                            <p className={`${themeClasses.textMuted} text-xs sm:text-sm`}>
                              {t('admin.referral.slotsForCode')}:{' '}
                              <span className={`${themeClasses.textPrimary} font-medium`}>
                                {referralOnboardingMaxUses == null ? t('admin.referral.slotsUnlimited') : referralOnboardingMaxUses}
                              </span>
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className={`border-t ${themeClasses.borderPrimary} pt-4 sm:pt-5 space-y-3`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h4 className={`${themeClasses.textPrimary} font-semibold text-sm sm:text-base`}>
                          {t('admin.referral.codesHistoryTitle')}
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:gap-3">
                          
                          <button
                            type="button"
                            onClick={() => fetchOnboardingCodesHistory()}
                            disabled={onboardingCodesHistoryLoading}
                            className={`text-left sm:text-center py-2 px-3 rounded-lg border ${themeClasses.borderPrimary} ${themeClasses.textPrimary} text-xs sm:text-sm font-medium touch-manipulation disabled:opacity-50 hover:opacity-90`}
                          >
                            {t('admin.referral.refreshCodes')}
                          </button>
                        </div>
                      </div>
                      {onboardingCodesHistoryLoading ? (
                        <p className={`${themeClasses.textMuted} text-sm py-2`}>{t('admin.referral.loadingOnboardingCodes')}</p>
                      ) : onboardingCodesHistory.length === 0 ? (
                        <p className={`${themeClasses.textMuted} text-sm py-2`}>{t('admin.referral.noOnboardingCodes')}</p>
                      ) : (
                        <ul className={`max-h-64 overflow-y-auto space-y-2 pr-1 ${themeClasses.textPrimary}`}>
                          {onboardingCodesHistory.map((row) => {
                            const used = row.used_count ?? 0;
                            const max = row.max_uses;
                            const usageStr = max == null ? `${used} · ${t('admin.referral.slotsUnlimited')}` : `${used} / ${max}`;
                            const now = Date.now();
                            let statusKey = 'active';
                            if (row.is_active === false) statusKey = 'inactive';
                            else if (row.valid_until && new Date(row.valid_until).getTime() <= now) statusKey = 'expired';
                            else if (row.valid_from && new Date(row.valid_from).getTime() > now) statusKey = 'pending';
                            else if (max != null && Number(max) >= 0 && used >= max) statusKey = 'exhausted';
                            const statusLabel =
                              statusKey === 'inactive' ? t('admin.referral.statusInactive')
                                : statusKey === 'expired' ? t('admin.referral.statusExpired')
                                  : statusKey === 'pending' ? t('admin.referral.statusPending')
                                    : statusKey === 'exhausted' ? t('admin.referral.statusExhausted')
                                      : t('admin.referral.statusActive');
                            return (
                              <li
                                key={row.id || row.code}
                                className={`rounded-lg border ${themeClasses.borderPrimary} px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between`}
                              >
                                <div className="min-w-0 space-y-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-mono text-base sm:text-lg font-semibold tracking-widest" dir="ltr">{row.code}</span>
                                    {onboardingCodesShowInactive ? (
                                      <span className={`text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full ${
                                        statusKey === 'active'
                                          ? 'bg-forest/20 text-forest-hover dark:text-forest-ondark'
                                          : 'bg-canvas0/20 text-ink-muted dark:text-ink-muted'
                                      }`}
                                      >
                                        {statusLabel}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className={`${themeClasses.textMuted} text-xs flex flex-wrap gap-x-3 gap-y-0.5`}>
                                    <span>{t('admin.referral.usageColumn')}: {usageStr}</span>
                                    <span>{t('admin.referral.created')}: {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</span>
                                    {row.valid_until ? (
                                      <span>{t('admin.referral.expires')}: {new Date(row.valid_until).toLocaleString()}</span>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 shrink-0 self-start sm:self-center">
                                  <button
                                    type="button"
                                    onClick={() => handleCopyOnboardingCodeString(row.code)}
                                    className={`py-2 px-3 rounded-lg border ${themeClasses.borderPrimary} ${themeClasses.textPrimary} text-xs font-medium touch-manipulation hover:opacity-90`}
                                  >
                                    {t('admin.referral.copyCode')}
                                  </button>
                                  {row.is_active !== false ? (
                                    <button
                                      type="button"
                                      onClick={() => handleDeactivateOnboardingCode(row.id)}
                                      disabled={!!deactivatingOnboardingId}
                                      className="py-2 px-3 rounded-lg border border-amber-600/50 text-amber-700 dark:text-amber-400 text-xs font-medium touch-manipulation hover:opacity-90 disabled:opacity-50"
                                    >
                                      {deactivatingOnboardingId === row.id
                                        ? (language === 'hebrew' ? 'מבטל…' : 'Deactivating…')
                                        : t('admin.referral.deactivateCode')}
                                    </button>
                                  ) : null}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-200">
                    <div className="text-center space-y-3 pt-2 mb-2">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark mb-2 border border-line dark:border-line-dark">
                        <Clock size={28} />
                      </div>
                      <h4 className={`text-xl font-medium ${themeClasses.textPrimary}`}>
                        {language === 'hebrew' ? 'קישור קמפיין מוגבל' : 'Limited Campaign Link'}
                      </h4>
                      <p className={`${themeClasses.textMuted} text-sm max-w-sm mx-auto leading-relaxed`}>
                        {t('admin.referral.limitedDesc')}
                      </p>
                    </div>

                    <div className={`p-5 sm:p-6 rounded-lg border transition-all ${isDarkMode ? 'bg-canvas-dark border-line-dark' : 'bg-canvas/50 border-line'}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                        <div className="space-y-1.5">
                          <label className={`text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>{t('admin.referral.maxClients')}</label>
                          <div className="relative">
                            <input
                              type="number"
                              min="1"
                              max="200"
                              value={maxClients}
                              onChange={(e) => setMaxClients(e.target.value)}
                              className={`w-full pl-11 pr-4 py-3 rounded-lg border transition-all outline-none focus:ring-2 focus:ring-forest font-medium ${isDarkMode ? 'bg-canvas-dark/80 border-line-dark text-white' : 'bg-white border-line text-ink focus:border-forest-ondark'}`}
                            />
                            <Activity className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${themeClasses.textMuted}`} />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className={`text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>{t('admin.referral.expiryDate')} <span className="text-red-500">*</span></label>
                          <input
                            type="date"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className={`w-full px-4 py-3 rounded-lg border transition-all outline-none focus:ring-2 focus:ring-forest font-medium ${isDarkMode ? 'bg-canvas-dark/80 border-line-dark text-white' : 'bg-white border-line text-ink focus:border-forest-ondark'}`}
                            required
                          />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2 md:col-span-1">
                          <label className={`text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>{t('admin.referral.expiryTime')}</label>
                          <input
                            type="time"
                            value={expiryTime}
                            onChange={(e) => setExpiryTime(e.target.value)}
                            className={`w-full px-4 py-3 rounded-lg border transition-all outline-none focus:ring-2 focus:ring-forest font-medium ${isDarkMode ? 'bg-canvas-dark/80 border-line-dark text-white' : 'bg-white border-line text-ink focus:border-forest-ondark'}`}
                          />
                        </div>
                      </div>

                      <label className={`flex items-center justify-between gap-4 p-4 rounded-lg border ${isDarkMode ? 'bg-canvas-dark/60 border-line-dark hover:border-line-dark' : 'bg-white border-line hover:border-line'} cursor-pointer transition-colors mb-6  group`}>
                        <div>
                          <p className={`text-sm font-bold ${themeClasses.textPrimary} group-hover:text-forest dark:group-hover:text-forest-ondark transition-colors`}>{t('admin.referral.skipPricingTitle')}</p>
                          <p className={`text-xs mt-1 ${themeClasses.textMuted}`}>{t('admin.referral.skipPricingDesc')}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={limitedSkipPricing}
                          onClick={() => setLimitedSkipPricing((v) => !v)}
                          className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-4 focus:ring-forest/20  ${limitedSkipPricing ? 'bg-forest' : isDarkMode ? 'bg-moss-dark' : 'bg-line'}`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${limitedSkipPricing ? (direction === 'rtl' ? '-translate-x-6' : 'translate-x-6') : (direction === 'rtl' ? '-translate-x-1' : 'translate-x-1')}`} />
                        </button>
                      </label>

                      <button
                        onClick={handleGenerateLimitedLink}
                        disabled={generatingLink || !expiryDate || !effectiveReferralManagerId}
                        className="w-full flex items-center justify-center gap-2 bg-forest hover:bg-forest-hover text-white font-bold py-3.5 px-6 rounded-lg transition-all  active:scale-[0.98] disabled:opacity-50 disabled:grayscale"
                      >
                        {generatingLink ? <RefreshCw className="w-5 h-5 animate-spin" /> : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>}
                        {generatingLink ? (language === 'hebrew' ? 'יוצר...' : 'Generating...') : t('admin.referral.generate')}
                      </button>
                    </div>

                    {/* Your active referral links */}
                    <div className="space-y-4 pt-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1">
                        <h4 className={`text-lg font-bold flex items-center gap-2 ${themeClasses.textPrimary}`}>
                          <span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-forest-ondark to-forest"></span>
                          {t('admin.referral.yourLinks')}
                        </h4>
                        {activeRegistrationLinks.length > 1 && (
                          <div className={`flex items-center gap-2 ${themeClasses.textMuted} text-xs font-semibold bg-canvas dark:bg-surface-dark px-3 py-1.5 rounded-full`}>
                            <span>{activeLinkCarouselIndex + 1}</span>
                            <span>/</span>
                            <span>{activeRegistrationLinks.length}</span>
                          </div>
                        )}
                      </div>

                      {registrationLinksLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className={`w-8 h-8 animate-spin ${themeClasses.textMuted} opacity-50`} />
                        </div>
                      ) : activeRegistrationLinks.length === 0 ? (
                        <div className={`text-center py-12 rounded-lg border border-dashed ${isDarkMode ? 'border-line-dark bg-surface-dark/20' : 'border-line bg-canvas'}`}>
                          <p className={`${themeClasses.textMuted} text-sm font-medium`}>{t('admin.referral.noLinks')}</p>
                        </div>
                      ) : (
                        <div className="flex items-stretch gap-2 sm:gap-4 relative group">
                          <button
                            type="button"
                            onClick={() => goToSlide(activeLinkCarouselIndex - 1)}
                            disabled={activeRegistrationLinks.length <= 1 || activeLinkCarouselIndex <= 0}
                            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 z-10 p-2.5 rounded-full  border  transition-all disabled:opacity-0 touch-manipulation active:scale-95 ${isDarkMode ? 'bg-surface-dark/90 border-line-dark text-white' : 'bg-white/90 border-line text-ink hover:scale-110'}`}
                            aria-label={t('admin.referral.prev')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">{direction === 'rtl' ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />}</svg>
                          </button>
                          
                          <div
                            ref={registrationLinksScrollRef}
                            className="flex-1 min-w-0 flex overflow-x-auto snap-x snap-mandatory scroll-smooth custom-scrollbar pb-4 -mb-4"
                            style={{ scrollSnapType: 'x mandatory' }}
                            onScroll={(e) => {
                              const el = e.target;
                              const w = el.clientWidth;
                              if (w <= 0) return;
                              const index = Math.round(el.scrollLeft / w);
                              setActiveLinkCarouselIndex(Math.min(Math.max(0, index), activeRegistrationLinks.length - 1));
                            }}
                          >
                            {activeRegistrationLinks.map((row, index) => {
                              const linkUrl = buildLimitedLinkUrl(row);
                              const maxSlots = row.max_slots ?? row.max_clients ?? 30;
                              const current = row.current_count ?? 0;
                              const slotsLeft = Math.max(0, maxSlots - current);
                              const usagePercent = Math.min(100, (current / maxSlots) * 100);
                              
                              return (
                                <div
                                  key={row.link_id || row.id || index}
                                  data-carousel-card
                                  className="flex-shrink-0 w-full snap-center px-1"
                                  style={{ minWidth: '100%' }}
                                >
                                  <div className={`p-5 sm:p-6 rounded-lg border transition-shadow  ${isDarkMode ? 'border-line-dark bg-surface-dark  shadow-black/40' : 'border-line bg-white '}`}>
                                    {editingLinkId === row.link_id ? (
                                      <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                          <h5 className={`text-sm font-bold ${themeClasses.textPrimary}`}>{language === 'hebrew' ? 'עריכת קישור' : 'Edit Link'}</h5>
                                          <button onClick={() => setEditingLinkId(null)} className="p-1.5 rounded-full hover:bg-canvas dark:hover:bg-moss-dark text-ink-muted">
                                            <X size={16} />
                                          </button>
                                        </div>
                                        <div className="space-y-3">
                                          <div>
                                            <label className={`block text-xs font-semibold mb-1 ${themeClasses.textSecondary}`}>{t('admin.referral.maxClients')}</label>
                                            <input
                                              type="number"
                                              min={current}
                                              value={editingLinkMaxClients}
                                              onChange={e => setEditingLinkMaxClients(e.target.value)}
                                              className={`w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-forest ${isDarkMode ? 'bg-canvas-dark/50 border-line-dark text-white' : 'bg-white border-line text-ink'}`}
                                            />
                                          </div>
                                          <label className={`flex items-center gap-2 text-sm font-medium cursor-pointer ${themeClasses.textPrimary}`}>
                                            <input
                                              type="checkbox"
                                              checked={editingLinkSkipPricing}
                                              onChange={e => setEditingLinkSkipPricing(e.target.checked)}
                                              className="w-4 h-4 rounded border-line text-forest focus:ring-forest bg-transparent"
                                            />
                                            {language === 'hebrew' ? 'לדלג על תמחור' : 'Skip Pricing Step'}
                                          </label>
                                        </div>
                                        <div className="pt-2 flex justify-end">
                                          <button
                                            onClick={() => handleSaveEditedLink(row.link_id)}
                                            disabled={editingLinkSaving}
                                            className="px-4 py-2 bg-forest hover:bg-forest text-white text-sm font-bold rounded-lg  transition-colors disabled:opacity-50"
                                          >
                                            {editingLinkSaving ? (language === 'hebrew' ? 'שומר...' : 'Saving...') : (language === 'hebrew' ? 'שמור' : 'Save')}
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                    <div className="flex items-start justify-between gap-2 mb-5">
                                      <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full bg-forest/10 text-forest dark:text-forest-ondark border border-forest/20  flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-forest animate-pulse"></span>
                                            {t('admin.referral.active')}
                                          </span>
                                          {row.skip_pricing && (
                                            <span className="text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 ">
                                              {t('admin.referral.skipPricingBadge') || (language === 'hebrew' ? 'ללא תמחור' : 'Skips Pricing')}
                                            </span>
                                          )}
                                        </div>
                                        <div className={`text-xs ${themeClasses.textMuted} flex items-center gap-1.5`}>
                                          <Clock size={12} />
                                          {t('admin.referral.created')} {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                                        </div>
                                      </div>
                                      
                                      <div className="flex flex-col items-end gap-1">
                                        <span className={`text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>{t('admin.referral.slotsLeft')}</span>
                                        <span className={`text-xl sm:text-2xl font-semibold ${slotsLeft === 0 ? 'text-amber-500' : 'text-forest'}`}>{slotsLeft}</span>
                                      </div>
                                    </div>
                                    
                                    {/* Progress Bar */}
                                    <div className="mb-6 space-y-2">
                                      <div className="flex items-center justify-between text-[10px] sm:text-xs font-bold">
                                        <span className={themeClasses.textMuted}>{current} {t('admin.referral.used').toLowerCase()}</span>
                                        <span className={themeClasses.textMuted}>{maxSlots} {t('admin.referral.max').toLowerCase()}</span>
                                      </div>
                                      <div className={`h-2.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-moss-dark/50' : 'bg-canvas '}`}>
                                        <div className="h-full bg-gradient-to-r from-forest-ondark to-forest rounded-full transition-all duration-1000 ease-out" style={{ width: `${usagePercent}%` }}></div>
                                      </div>
                                    </div>
                                    
                                    {row.expires_at && (
                                      <div className={`mb-5 p-3 rounded-lg border flex items-center gap-3 ${isDarkMode ? 'bg-surface-dark/50 border-line-dark' : 'bg-canvas border-canvas'}`}>
                                        <AlertCircle size={16} className="text-amber-500 shrink-0" />
                                        <span className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                                          {t('admin.referral.expires')}: <span className={themeClasses.textPrimary}>{new Date(row.expires_at).toLocaleString()}</span>
                                        </span>
                                      </div>
                                    )}

                                    <div className="flex flex-col sm:flex-row gap-3">
                                      <div className="flex-1 flex flex-col gap-2 min-w-0">
                                        <div className={`flex items-center gap-2 p-1.5 rounded-lg border focus-within:ring-2 focus-within:ring-forest/20 transition-all ${isDarkMode ? 'bg-canvas-dark/50 border-line-dark' : 'bg-white border-line'}`}>
                                          <input
                                            type="text"
                                            readOnly
                                            value={linkUrl}
                                            className={`flex-1 bg-transparent px-3 py-1.5 outline-none text-xs font-medium ${themeClasses.textPrimary} truncate`}
                                          />
                                          <button
                                            onClick={() => handleCopyReferralLink(linkUrl)}
                                            className="shrink-0 p-2 rounded-lg bg-forest-faint text-forest hover:bg-forest-faint dark:bg-forest/10 dark:text-forest-ondark dark:hover:bg-forest/20 transition-colors"
                                            title={t('admin.referral.copy')}
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                                          </button>
                                        </div>
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => {
                                              setEditingLinkMaxClients(maxSlots);
                                              setEditingLinkSkipPricing(!!row.skip_pricing);
                                              setEditingLinkId(row.link_id);
                                            }}
                                            className={`flex-1 py-2.5 rounded-lg border text-xs font-bold transition-all active:scale-95 flex items-center justify-center gap-2 ${isDarkMode ? 'border-blue-500/20 text-blue-400 hover:bg-blue-500/10' : 'border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300'}`}
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                            {language === 'hebrew' ? 'ערוך' : 'Edit'}
                                          </button>
                                          <button
                                            onClick={() => handleDeactivateLink(row.link_id)}
                                            disabled={!!deactivatingLinkId}
                                            className={`flex-1 py-2.5 rounded-lg border text-xs font-bold transition-all active:scale-95 flex items-center justify-center gap-2 ${isDarkMode ? 'border-red-500/20 text-red-400 hover:bg-red-500/10' : 'border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'} disabled:opacity-50`}
                                          >
                                            <Ban size={14} />
                                            {deactivatingLinkId === row.link_id ? (language === 'hebrew' ? 'מבטל…' : 'Deactivating…') : t('admin.referral.deactivate')}
                                          </button>
                                        </div>
                                      </div>
                                      <div className="shrink-0 flex flex-col items-center gap-2 justify-center">
                                        <div ref={(el) => { if (el) limitedQrCodeRefs.current[row.link_id] = el; }} className={`p-2 bg-white rounded-lg ${isDarkMode ? '' : 'border border-canvas '}`}>
                                          <SimpleQRCode value={linkUrl} size={90} />
                                        </div>
                                        <button
                                          onClick={() => handleCopyQRCodeImage(row.link_id)}
                                          className={`text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1 ${themeClasses.textMuted} hover:${themeClasses.textPrimary}`}
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                                          {t('admin.referral.copyQR')}
                                        </button>
                                      </div>
                                    </div>
                                    </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          <button
                            type="button"
                            onClick={() => goToSlide(activeLinkCarouselIndex + 1)}
                            disabled={activeRegistrationLinks.length <= 1 || activeLinkCarouselIndex >= activeRegistrationLinks.length - 1}
                            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 z-10 p-2.5 rounded-full  border  transition-all disabled:opacity-0 touch-manipulation active:scale-95 ${isDarkMode ? 'bg-surface-dark/90 border-line-dark text-white' : 'bg-white/90 border-line text-ink hover:scale-110'}`}
                            aria-label={t('admin.referral.next')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">{direction === 'rtl' ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />}</svg>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Expired / Inactive Links Section */}
                    <div className="pt-6 mt-6 border-t border-line dark:border-line-dark">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className={`text-base font-bold ${themeClasses.textPrimary}`}>
                          {language === 'hebrew' ? 'קישורים לא פעילים / פגי תוקף' : 'Inactive / Expired Links'}
                        </h5>
                        <button
                          onClick={() => setRegistrationLinksShowInactive(prev => !prev)}
                          className={`text-sm px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                            registrationLinksShowInactive
                              ? 'bg-forest-faint text-forest border-forest/20 dark:bg-forest/10 dark:text-forest-ondark dark:border-forest/20'
                              : 'bg-white text-ink-muted border-line hover:bg-canvas dark:bg-surface-dark dark:text-ink-muted-dark dark:border-line-dark dark:hover:bg-moss-dark'
                          }`}
                        >
                          {registrationLinksShowInactive 
                            ? (language === 'hebrew' ? 'הסתר קישורים ישנים' : 'Hide Old Links')
                            : (language === 'hebrew' ? 'הצג קישורים ישנים' : 'Load Old Links')}
                        </button>
                      </div>

                      {registrationLinksShowInactive && (
                        <div className="overflow-x-auto rounded-lg border border-line dark:border-line-dark bg-white dark:bg-surface-dark  custom-scrollbar">
                          <table className={`w-full text-${direction === 'rtl' ? 'right' : 'left'} border-collapse text-sm`}>
                            <thead className="bg-canvas dark:bg-moss-dark/50">
                              <tr>
                                <th className={`p-3 font-semibold ${themeClasses.textSecondary}`}>{t('admin.referral.created')}</th>
                                <th className={`p-3 font-semibold ${themeClasses.textSecondary}`}>{t('admin.referral.max')}</th>
                                <th className={`p-3 font-semibold ${themeClasses.textSecondary}`}>{t('admin.referral.used')}</th>
                                <th className={`p-3 font-semibold ${themeClasses.textSecondary}`}>{t('admin.referral.expires')}</th>
                                <th className={`p-3 font-semibold ${themeClasses.textSecondary}`}>{language === 'hebrew' ? 'דילוג על תמחור' : 'Skip Pricing'}</th>
                                <th className={`p-3 font-semibold ${themeClasses.textSecondary}`}>{language === 'hebrew' ? 'סטטוס' : 'Status'}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-line dark:divide-line-dark">
                              {registrationLinks.filter(r => r.is_active === false || (r.expires_at && new Date(r.expires_at) < new Date(new Date().setHours(0, 0, 0, 0)))).map((row) => (
                                <tr key={row.link_id || row.id} className="hover:bg-canvas dark:hover:bg-moss-dark/20">
                                  <td className={`p-3 ${themeClasses.textPrimary}`}>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                                  <td className={`p-3 ${themeClasses.textPrimary}`}>{row.max_slots ?? row.max_clients ?? 30}</td>
                                  <td className={`p-3 ${themeClasses.textPrimary}`}>{row.current_count ?? 0}</td>
                                  <td className={`p-3 ${themeClasses.textPrimary}`}>{row.expires_at ? new Date(row.expires_at).toLocaleString() : '—'}</td>
                                  <td className={`p-3 ${themeClasses.textPrimary}`}>
                                    {row.skip_pricing 
                                      ? <span className="text-forest font-medium">{language === 'hebrew' ? 'כן' : 'Yes'}</span> 
                                      : <span className="text-ink-muted">{language === 'hebrew' ? 'לא' : 'No'}</span>}
                                  </td>
                                  <td className="p-3">
                                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-canvas text-ink-muted dark:bg-moss-dark dark:text-ink-muted">
                                      {row.is_active === false ? (language === 'hebrew' ? 'בוטל' : 'Deactivated') : (language === 'hebrew' ? 'פג תוקף' : 'Expired')}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                              {registrationLinks.filter(r => r.is_active === false || (r.expires_at && new Date(r.expires_at) < new Date(new Date().setHours(0, 0, 0, 0)))).length === 0 && (
                                <tr>
                                  <td colSpan="6" className={`p-6 text-center text-ink-muted`}>
                                    {language === 'hebrew' ? 'לא נמצאו קישורים ישנים.' : 'No old links found.'}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </aside>
        </div>
      )}

      {/* Help (?) tooltip portal - renders over table/KPI so never clipped */}
      {helpTooltipActive && helpTooltipRect && createPortal(
        <div
          id="help-tooltip-portal"
          className={`rounded-lg px-3 py-2.5 text-xs  border z-[9999] ${
            helpTooltipActive === 'admin.help.onboardingProgress'
              ? `max-w-md max-h-[min(70vh,520px)] overflow-y-auto whitespace-pre-line ${isDarkMode ? 'bg-surface-dark border-line-dark text-ink-dark' : 'bg-white border-line text-surface-dark'}`
              : `max-w-[280px] whitespace-normal ${isDarkMode ? 'bg-surface-dark border-line-dark text-ink-dark' : 'bg-white border-line text-surface-dark'}`
          }`}
          style={{
            position: 'fixed',
            left: direction === 'rtl' ? 'auto' : helpTooltipRect.left,
            right: direction === 'rtl' ? window.innerWidth - helpTooltipRect.right : 'auto',
            top: helpTooltipRect.bottom + 6,
          }}
          onMouseEnter={() => setHelpTooltipActive(helpTooltipActive)}
          onMouseLeave={() => setHelpTooltipActive(null)}
        >
          {helpTooltipActive === 'admin.help.clientAssignments' && companyName
            ? t('admin.help.clientAssignmentsWithCompany').replace('{{companyName}}', companyName)
            : helpTooltipActive.startsWith('admin.help.assignedToAdminReadonly')
            ? t('admin.help.assignedToAdminReadonly')
            : helpTooltipActive.startsWith('admin.help.digitalOnlySubscription__')
            ? t('admin.help.digitalOnlySubscription')
            : t(helpTooltipActive)}
        </div>,
        document.body
      )}

      {/* Chat preview tooltip portal - renders over table, opens up or down by space */}
      {chatTooltipClientId && chatTooltipRect && (() => {
        const chatClient = filteredClients.find((c) => c.id === chatTooltipClientId);
        if (!chatClient?.chatPreview?.length) return null;
        const popupHeight = 220;
        const gap = 6;
        const openAbove = chatTooltipRect.bottom + popupHeight + gap > window.innerHeight;
        return createPortal(
          <div
            id="chat-preview-portal"
            className={`w-64 max-h-[280px] overflow-y-auto rounded-lg  border z-[9999] ${themeClasses.borderPrimary} ${isDarkMode ? 'bg-surface-dark' : 'bg-white'} p-3 space-y-2 custom-scrollbar`}
            style={{
              position: 'fixed',
              left: direction === 'rtl' ? 'auto' : chatTooltipRect.left,
              right: direction === 'rtl' ? window.innerWidth - chatTooltipRect.right : 'auto',
              ...(openAbove
                ? { bottom: window.innerHeight - chatTooltipRect.top + gap, top: 'auto' }
                : { top: chatTooltipRect.bottom + gap, bottom: 'auto' }),
            }}
            onMouseEnter={() => setChatTooltipClientId(chatTooltipClientId)}
            onMouseLeave={() => setChatTooltipClientId(null)}
          >
            {chatClient.chatPreview.slice(-3).map((msg, idx) => (
              <div key={idx} className="text-xs">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={msg.role === 'user' ? 'text-blue-500' : 'text-forest'}>
                    {msg.role === 'user' ? '👤' : '🤖'}
                  </span>
                  <span className={`text-[10px] ${themeClasses.textMuted}`}>{formatTimeAgo(msg.created_at)}</span>
                </div>
                <div className={`${themeClasses.textSecondary} ${isDarkMode ? 'bg-moss-dark/60' : 'bg-canvas'} rounded px-2 py-1 border ${themeClasses.borderPrimary} break-words line-clamp-3`}>
                  {renderPreviewContent(msg)}
                </div>
              </div>
            ))}
          </div>,
          document.body
        );
      })()}
    </div>
  );
}

function SimpleQRCode({ value, size = 200 }) {
  if (!value) return null;
  return (
    <QRCodeSVG
      value={value}
      size={size}
      level="H"
      includeMargin={true}
      bgColor="#ffffff"
      fgColor="#000000"
    />
  );
}

export default AdminDashboardPage;
